import dd from 'dingtalk-jsapi';
import './style.css';

const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const currentMonth = () => today().slice(0, 7);
const normalizeMonth = (value) => /^\d{4}-\d{2}$/.test(value || '') ? value : currentMonth();
const money = (value) => Number(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char]);
const roleNames = { sales: '业务员', purchase: '采购', logistics: '物流', admin: '外贸经理' };
const expenseCategoryNames = { travel: '差旅费', transport: '交通费', meals: '餐饮费', office: '办公费', freight: '物流费', client: '客户招待', other: '其他' };
const expenseStatusNames = { pending: '待审批', approved: '已通过', rejected: '已驳回' };
const statusMeta = {
  pending_purchase: { label: '待采购', className: 'pending_purchase' },
  purchasing: { label: '采购中', className: 'purchasing' },
  pending_payment: { label: '待付款', className: 'payment' },
  purchased: { label: '待发货', className: 'purchased' },
  shipped: { label: '已发货', className: 'shipped' },
  cancelled: { label: '已取消', className: 'cancelled' }
};

const tradeTerms = {
  EXW: { group: 'E组 · 启运类', name: '工厂交货', desc: '卖方责任最小，买方承担从卖方工厂到目的地的全部运输费用和风险' },
  FCA: { group: 'F组 · 主运费未付类', name: '货交承运人', desc: '卖方在指定地点完成交货后，风险转移给买方' },
  FAS: { group: 'F组 · 主运费未付类', name: '装运港船边交货', desc: '卖方在装运港船边交货后，风险转移给买方' },
  FOB: { group: 'F组 · 主运费未付类', name: '装运港船上交货', desc: '卖方将货物装上船后，风险转移给买方' },
  CFR: { group: 'C组 · 主运费已付类', name: '成本加运费', desc: '卖方负责运输安排，但风险在装运后转移' },
  CIF: { group: 'C组 · 主运费已付类', name: '成本加保险加运费', desc: '卖方负责运输与保险安排，风险在装运后转移' },
  CPT: { group: 'C组 · 主运费已付类', name: '运费付至', desc: '卖方支付至目的地的运费，风险在装运后转移' },
  CIP: { group: 'C组 · 主运费已付类', name: '运费及保险付至', desc: '卖方支付运费并投保，风险在装运后转移' },
  DPU: { group: 'D组 · 到达类', name: '卸货地交货', desc: '卖方承担货物运至卸货地的全部风险与费用' },
  DAP: { group: 'D组 · 到达类', name: '目的地交货', desc: '卖方承担货物运至目的地的全部风险与费用' },
  DDP: { group: 'D组 · 到达类', name: '完税后交货', desc: '卖方承担全部风险与费用，并办理进口完税' }
};

const tradeTermNoteText = (code) => {
  const term = tradeTerms[code];
  return term ? `${code} · ${term.name}：${term.desc}` : '选择后显示条款说明';
};

const state = {
  token: localStorage.getItem('hangji_pc_session') || '',
  user: null,
  orders: [],
  orderFilter: 'all',
  ownerFilter: '',
  orderDateFrom: '',
  orderDateTo: '',
  orderViewItems: null,
  search: '',
  currentView: 'dashboard',
  currentDetail: null,
  members: [],
  rates: null,
  performance: null,
  performanceEmployees: [],
  leaderboardReport: null,
  performanceMonth: currentMonth(),
  productDrafts: [],
  paymentAttachments: [],
  purchaseAttachments: [],
  shipmentAttachments: [],
  adminPaymentAttachments: [],
  adminPurchaseAttachments: [],
  expenseAttachments: [],
  expenses: [],
  shipmentOrderId: '',
  shipmentMode: 'draft',
  shipmentAction: 'dispatch',
  adminEditProducts: [],
  adminEditIsSuperAdmin: false,
  busy: false
};

const previewMode = ['127.0.0.1', 'localhost'].includes(window.location.hostname)
  && new URLSearchParams(window.location.search).get('preview') === '1';

const emptyProduct = () => ({
  sku: '', name: '', variant: '', unitsPerCarton: 0, cartons: 0, weight: 0, volume: 0,
  quantity: 1, unitPrice: 0, purchaseCost: 0, images: []
});

const toast = (message) => {
  const element = $('#toast');
  element.textContent = message;
  element.classList.add('show');
  clearTimeout(toast.timer);
  toast.timer = setTimeout(() => element.classList.remove('show'), 2800);
};

// 钉钉 PC WebView 对原生 input[type="month"] 支持不完整，改用可点击的年/月下拉框。
// 隐藏 input 保留原有读取接口，月份变化时继续派发 change 事件。
const setMonthPickerValue = (picker, value, emit = false) => {
  const normalized = normalizeMonth(value);
  const [year, month] = normalized.split('-');
  const yearSelect = $('[data-month-year]', picker);
  if (![...yearSelect.options].some((option) => option.value === year)) {
    const option = new Option(`${year} 年`, year);
    yearSelect.add(option);
  }
  yearSelect.value = year;
  $('[data-month-number]', picker).value = month;
  const input = $(`#${picker.dataset.monthPicker}`);
  input.value = normalized;
  if (emit) input.dispatchEvent(new Event('change', { bubbles: true }));
};

const initializeMonthPickers = () => {
  $$('[data-month-picker]').forEach((picker) => {
    const input = $(`#${picker.dataset.monthPicker}`);
    const initial = normalizeMonth(input.value);
    const initialYear = Number(initial.slice(0, 4));
    $('[data-month-year]', picker).innerHTML = Array.from({ length: 16 }, (_, index) => {
      const year = initialYear - 10 + index;
      return `<option value="${year}">${year} 年</option>`;
    }).join('');
    $('[data-month-number]', picker).innerHTML = Array.from({ length: 12 }, (_, index) => {
      const month = String(index + 1).padStart(2, '0');
      return `<option value="${month}">${index + 1} 月</option>`;
    }).join('');
    setMonthPickerValue(picker, initial);
    picker.addEventListener('change', (event) => {
      if (!event.target.matches('select')) return;
      setMonthPickerValue(picker, `${$('[data-month-year]', picker).value}-${$('[data-month-number]', picker).value}`, true);
    });
    picker.addEventListener('click', (event) => {
      const button = event.target.closest('[data-month-step]');
      if (!button) return;
      const [year, month] = normalizeMonth(input.value).split('-').map(Number);
      const shifted = new Date(year, month - 1 + Number(button.dataset.monthStep), 1);
      setMonthPickerValue(picker, `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}`, true);
    });
  });
};

const setError = (message = '') => {
  const banner = $('#error-banner');
  banner.textContent = message;
  banner.classList.toggle('is-hidden', !message);
};

// 钉钉 PC 客户端会拦截 window.confirm（静默返回 false，连弹窗都不显示），
// 删除等破坏性操作统一改用应用内确认弹窗。
const confirmRequest = { resolve: null };

const askConfirm = ({ title = '确认操作', message = '', confirmText = '确定', cancelText = '取消', danger = true } = {}) => {
  const dialog = $('#confirm-dialog');
  // 极端环境下 <dialog> 不可用时回退到原生确认框，避免破坏性操作静默失效。
  if (!dialog || typeof dialog.showModal !== 'function') return Promise.resolve(window.confirm(`${title}\n\n${message}`));
  $('#confirm-title').textContent = title;
  $('#confirm-message').textContent = message;
  const accept = $('#confirm-accept');
  accept.textContent = confirmText;
  accept.className = `button ${danger ? 'danger' : 'primary'}`;
  $('#confirm-cancel').textContent = cancelText;
  dialog.returnValue = '';
  try {
    dialog.showModal();
  } catch (_error) {
    return Promise.resolve(window.confirm(`${title}\n\n${message}`));
  }
  return new Promise((resolve) => { confirmRequest.resolve = resolve; });
};

const setBusy = (busy, label) => {
  state.busy = busy;
  ['#create-button', '#save-order', '#save-shipment', '#save-expense', '#refresh-button', '#import-pi'].forEach((selector) => {
    const button = $(selector);
    if (button) button.disabled = busy;
  });
  if (label) toast(label);
};

const api = async (path, options = {}) => {
  const headers = new Headers(options.headers || {});
  if (state.token) headers.set('Authorization', `Bearer ${state.token}`);
  if (options.body && !(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  const response = await fetch(path, { ...options, headers });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401 && path !== '/api/auth/dingtalk') logout(false);
    throw new Error(body?.error?.message || `请求失败（${response.status}）`);
  }
  return body;
};

const uploadImage = async (file, category) => {
  const form = new FormData();
  form.append('file', file);
  form.append('category', category);
  form.append('originalName', file.name);
  const result = await api('/api/uploads/image', { method: 'POST', body: form });
  return result.attachment;
};

const uploadExpenseFile = async (file) => {
  const form = new FormData();
  form.append('file', file);
  form.append('category', 'expense');
  form.append('originalName', file.name);
  const result = await api('/api/uploads/file', { method: 'POST', body: form });
  return result.attachment;
};

const dingTalkAuthCode = async (corpId) => {
  if (!corpId) throw new Error('服务端未配置 DING_CORP_ID');
  if (!dd) throw new Error('钉钉客户端 SDK 加载失败');
  console.info('钉钉 PC 免登环境', {
    platform: dd.env?.platform || 'unknown',
    hasRuntimeAuth: typeof dd.runtime?.permission?.requestAuthCode === 'function',
    hasGetAuthCode: typeof dd.getAuthCode === 'function',
    userAgentHasDingTalk: /DingTalk/i.test(navigator.userAgent)
  });
  if (dd.env?.platform === 'notInDingTalk' && !/DingTalk/i.test(navigator.userAgent)) {
    throw new Error('当前工作台使用系统浏览器打开，无法调用钉钉 H5 免登；请改为钉钉内置窗口，或接入网页登录/扫码登录');
  }

  // 不单独使用 dd.env.platform 判断：钉钉 UA 存在时即使平台字段误报，仍尝试 JSBridge；
  // 只有平台和浏览器 UA 都确认是外部环境时才停止调用。
  await Promise.race([
    new Promise((resolve, reject) => {
      let ready = false;
      const onReady = () => { if (!ready) { ready = true; resolve(); } };
      const result = dd.ready(onReady);
      if (result && typeof result.then === 'function') result.then(onReady, reject);
    }),
    new Promise((_, reject) => setTimeout(() => reject(new Error('钉钉 JSAPI 初始化超时')), 8000))
  ]);

  const invokeAuthApi = (method, owner) => new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('获取免登授权码超时'));
    }, 10000);
    const onSuccess = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result?.authCode || result?.code);
    };
    const onFail = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new Error(error?.errorMessage || error?.message || '获取免登授权码失败'));
    };
    const request = method.call(owner, {
      corpId,
      success: onSuccess,
      fail: onFail
    });
    if (request && typeof request.then === 'function') request.then(onSuccess, onFail);
  });

  const attempts = [];
  const legacyMethod = dd.runtime?.permission?.requestAuthCode;
  if (typeof legacyMethod === 'function') {
    try {
      return await invokeAuthApi(legacyMethod, dd.runtime.permission);
    } catch (error) {
      attempts.push(error.message);
    }
  }
  if (typeof dd.getAuthCode === 'function') {
    try {
      return await invokeAuthApi(dd.getAuthCode, dd);
    } catch (error) {
      attempts.push(error.message);
    }
  }
  const platform = dd.env?.platform || 'unknown';
  throw new Error(`无法调用钉钉网页免登（platform=${platform}）：${attempts.join('；') || '客户端未提供免登接口'}`);
};

async function authenticate() {
  const message = $('#auth-message');
  message.textContent = '正在验证当前会话…';
  if (state.token) {
    try {
      const result = await api('/api/auth/me');
      state.user = result.user;
      return enterApp();
    } catch (_error) {
      state.token = '';
      localStorage.removeItem('hangji_pc_session');
    }
  }
  try {
    message.textContent = '正在连接钉钉身份…';
    const config = await api('/api/public-config');
    const code = await dingTalkAuthCode(config.corpId);
    if (!code) throw new Error('钉钉未返回免登授权码');
    const result = await api('/api/auth/dingtalk', {
      method: 'POST',
      body: JSON.stringify({ code })
    });
    state.token = result.token;
    state.user = result.user;
    localStorage.setItem('hangji_pc_session', state.token);
    enterApp();
  } catch (error) {
    console.error('PC 端免登失败', error);
    message.textContent = error.message;
    $('#auth-retry').classList.remove('is-hidden');
  }
}

function enterApp() {
  $('#auth-screen').classList.add('is-hidden');
  $('#app').classList.remove('is-hidden');
  renderAccount();
  setMonthPickerValue($('[data-month-picker="performance-month"]'), state.performanceMonth);
  if (previewMode) {
    renderAll();
    renderLeaderboard();
  }
  else {
    loadOrders();
    loadExchangeRates();
    loadLeaderboard();
  }
}

function logout(reload = true) {
  state.token = '';
  state.user = null;
  localStorage.removeItem('hangji_pc_session');
  if (reload) window.location.reload();
}

function renderAccount() {
  const user = state.user;
  $('#account-name').textContent = user.name || '钉钉用户';
  $('#account-role').textContent = `${roleNames[user.role] || user.role}${user.title ? ` · ${user.title}` : ''}`;
  $('#account-avatar').innerHTML = user.avatarUrl
    ? `<img src="${escapeHtml(user.avatarUrl)}" alt="" />`
    : escapeHtml((user.name || '航').slice(0, 1));
  $$('.admin-only').forEach((element) => element.classList.toggle('is-hidden', user.role !== 'admin'));
  $$('.super-admin-only').forEach((element) => element.classList.toggle('is-hidden', !user.isDingAdmin));
  $$('.sales-only').forEach((element) => element.classList.toggle('is-hidden', !['sales', 'admin'].includes(user.role)));
  $('#expense-nav b').textContent = user.isDingAdmin ? '报销管理' : '费用报销';
}

async function loadOrders() {
  setError('');
  try {
    const result = await api('/api/orders?page=1&pageSize=100');
    state.orders = result.items || [];
    renderAll();
  } catch (error) {
    console.error('订单读取失败', error);
    setError(`订单读取失败：${error.message}`);
  }
}

async function loadOrderView() {
  const { orderDateFrom, orderDateTo } = state;
  if (orderDateFrom && orderDateTo && orderDateTo < orderDateFrom) {
    toast('结束日期不能早于开始日期');
    return;
  }
  if (!orderDateFrom && !orderDateTo) {
    state.orderViewItems = null;
    renderOrders();
    return;
  }
  if (previewMode) {
    state.orderViewItems = state.orders.filter((order) => {
      const date = String(order.createdAt || '').slice(0, 10);
      return (!orderDateFrom || date >= orderDateFrom) && (!orderDateTo || date <= orderDateTo);
    });
    renderOrders();
    return;
  }
  $('#orders-body').innerHTML = '<tr><td colspan="8"><div class="loading-line"></div></td></tr>';
  try {
    const query = new URLSearchParams({ page: '1', pageSize: '100' });
    if (orderDateFrom) query.set('dateFrom', orderDateFrom);
    if (orderDateTo) query.set('dateTo', orderDateTo);
    const result = await api(`/api/orders?${query.toString()}`);
    state.orderViewItems = result.items || [];
    renderOrders();
  } catch (error) {
    toast(`日期筛选失败：${error.message}`);
  }
}

async function loadExchangeRates() {
  try {
    const result = await api('/api/exchange-rates/usd-cny');
    state.rates = result.rates;
    renderExchangeRates();
  } catch (error) {
    console.error('汇率读取失败', error);
    $('#rate-month-start').textContent = '暂不可用';
    $('#rate-today').textContent = '暂不可用';
  }
}

async function loadLeaderboard() {
  try {
    state.leaderboardReport = await api(`/api/performance/leaderboard?month=${encodeURIComponent(currentMonth())}`);
    renderLeaderboard();
  } catch (error) {
    console.error('排行榜读取失败', error);
    $('#sales-champion').innerHTML = '<span>排行榜暂时不可用</span>';
    $('#sales-leaderboard').innerHTML = '';
    $('#my-performance-content').innerHTML = '<span>个人业绩暂时不可用</span>';
  }
}

const rankingAvatar = (item, className) => item.avatarUrl
  ? `<div class="${className}"><img src="${escapeHtml(item.avatarUrl)}" alt="" /></div>`
  : `<div class="${className}">${escapeHtml((item.name || '航').slice(0, 1))}</div>`;

function renderLeaderboard() {
  const report = state.leaderboardReport;
  if (!report) return;
  const champion = report.champion;
  $('#sales-champion').innerHTML = champion ? `
    ${rankingAvatar(champion, 'sales-champion-avatar')}
    <div class="sales-champion-main"><small>本月销冠</small><strong>${escapeHtml(champion.name)}</strong><span>${champion.orderCount} 笔订单 · 利润 ¥${money(champion.profitCny)}</span></div>
    <div class="sales-champion-value"><strong>¥${money(champion.salesCny)}</strong><small>折算销售额</small></div>` : '<span>本月还没有销售业绩</span>';
  $('#sales-leaderboard').innerHTML = (report.leaders || []).slice(0, 6).map((item) => `
    <div class="sales-rank-row">
      <span class="sales-rank-number">${item.rank}</span>${rankingAvatar(item, 'sales-rank-avatar')}
      <div class="sales-rank-main"><strong>${escapeHtml(item.name)}</strong><small>${item.orderCount} 笔订单${item.title ? ` · ${escapeHtml(item.title)}` : ''}</small></div>
      <span class="sales-rank-value">¥${money(item.salesCny)}</span>
    </div>`).join('') || '<div class="empty-state"><strong>暂无排名</strong></div>';
  const mine = report.me;
  $('#my-performance-content').innerHTML = mine ? `
    <span class="my-performance-rank">本月第 ${mine.rank} 名 · ${mine.orderCount} 笔订单</span>
    <div class="my-performance-total"><strong>¥${money(mine.salesCny)}</strong><span>折算人民币销售额</span></div>
    <div class="my-performance-stats"><div><small>本月利润</small><strong>¥${money(mine.profitCny)}</strong></div><div><small>预计提成</small><strong>¥${money(mine.commissionCny)}</strong></div></div>` : '<span>当前角色没有个人销售业绩</span>';
}

function renderExchangeRates() {
  if (!state.rates) return;
  const first = Number(state.rates.monthStart.rate);
  const latest = Number(state.rates.today.rate);
  const change = latest - first;
  $('#rate-month-start').textContent = first.toFixed(4);
  $('#rate-month-date').textContent = state.rates.monthStart.date;
  $('#rate-today').textContent = latest.toFixed(4);
  $('#rate-today-date').textContent = state.rates.today.date;
  $('#rate-change').textContent = `${change >= 0 ? '+' : ''}${change.toFixed(4)}`;
  $('#rate-change').className = change > 0 ? 'positive' : change < 0 ? 'negative' : '';
}

async function loadPerformance() {
  if (!['sales', 'admin'].includes(state.user?.role)) return;
  if (previewMode) {
    renderPerformance();
    return;
  }
  $('#performance-body').innerHTML = '<tr><td colspan="12"><div class="loading-line"></div></td></tr>';
  try {
    const query = new URLSearchParams({ month: state.performanceMonth });
    if (state.ownerFilter) query.set('ownerUserId', state.ownerFilter);
    state.performance = await api(`/api/performance/monthly?${query.toString()}`);
    renderPerformance();
  } catch (error) {
    $('#performance-body').innerHTML = `<tr><td colspan="12">业绩读取失败：${escapeHtml(error.message)}</td></tr>`;
  }
}

function renderPerformance() {
  const report = state.performance;
  if (!report) return;
  $('#performance-orders').textContent = report.summary.orderCount;
  $('#performance-received').textContent = `¥${money(report.summary.revenueCny)}`;
  $('#performance-profit').textContent = `¥${money(report.summary.profitCny)}`;
  $('#performance-commission').textContent = `¥${money(report.summary.completedCommissionCny)}`;
  $('#performance-commission-note').textContent = `全部订单预计 ¥${money(report.summary.commissionCny)} · 下月工资扣款 ¥${money(report.summary.nextMonthSalaryDeductionCny)}`;
  $('#performance-rate-note').textContent = report.exchangeRate
    ? `统一按 ${report.exchangeRate.date} 汇率 ${Number(report.exchangeRate.rate).toFixed(4)} 折算`
    : '美元金额统一按当月 1 日汇率折算';
  const formulaRows = report.profitFormula || [];
  $('#profit-formula').classList.toggle('is-hidden', formulaRows.length === 0);
  $('#profit-formula-body').innerHTML = formulaRows.map((row) => {
    const showMinus = row.key === 'profit' || row.key === 'commission' ? Number(row.value) < 0 : row.negative;
    return `
    <div class="profit-formula-row ${['profit', 'net'].includes(row.key) ? 'is-strong' : ''}" data-formula-key="${escapeHtml(row.key)}">
      <span class="profit-formula-label">${escapeHtml(row.label)}</span>
      <span class="profit-formula-expression">${escapeHtml(row.expression)}${row.note ? `<small>${escapeHtml(row.note)}</small>` : ''}</span>
      <span class="profit-formula-value ${showMinus ? 'is-negative' : ''}">${showMinus ? '−' : ''}¥${money(row.value)}</span>
    </div>`;
  }).join('');
  $('#performance-body').innerHTML = report.items.map((item) => `<tr class="performance-main-row" data-order-id="${item.id}">
    <td><div class="performance-order"><strong>${escapeHtml(item.customerName)}</strong><span>${escapeHtml(item.orderNo)} · ${escapeHtml(item.orderDate)}</span></div></td>
    <td class="admin-only">${escapeHtml(item.ownerName)}</td>
    <td>${escapeHtml(item.currency)} ${money(item.orderAmount)}</td>
    <td>${item.receivedUsd == null ? (item.revenueCny == null ? '待汇率' : `≈ ¥${money(item.revenueCny)}`) : `¥${money(item.revenueCny)}`}</td>
    <td>CNY ${money(item.productCost)}</td>
    <td>${escapeHtml(item.currency)} ${money(item.sellerFreight)}</td>
    <td>CNY ${money(item.shippingFreight)}</td>
    <td class="${Number(item.profitCny) >= 0 ? 'profit-positive' : 'profit-negative'}">${item.profitCny == null ? '待汇率' : `¥${money(item.profitCny)}`}</td>
    <td><strong>${item.commissionCny == null ? '待汇率' : `¥${money(item.commissionCny)}`}</strong><small> · ${Number(item.commissionRatePercent).toFixed(2)}%</small></td>
    <td class="${Number(item.nextMonthSalaryDeductionCny) > 0 ? 'profit-negative' : ''}">${item.nextMonthSalaryDeductionCny == null ? '待汇率' : `¥${money(item.nextMonthSalaryDeductionCny)}`}</td>
    <td>${escapeHtml(item.freightForwarder || '未填写')}</td>
    <td><span class="completion-badge ${item.isCompleted ? 'done' : ''}">${item.isCompleted ? '已完结' : '未完结'}</span></td>
  </tr>
  <tr class="performance-formula-row" data-order-id="${item.id}"><td colspan="12">${item.profitFormula || '缺少汇率，暂无法折算人民币'}</td></tr>`).join('') || '<tr><td colspan="12">该月暂无订单</td></tr>';
  const warning = report.warnings?.join('；') || '';
  $('#performance-warning').textContent = warning;
  $('#performance-warning').classList.toggle('is-hidden', !warning);
  renderAccount();
}

const deadlineDays = (value) => {
  if (!value) return 9999;
  const date = String(value).slice(0, 10);
  return Math.ceil((new Date(`${date}T23:59:59`).getTime() - Date.now()) / 86400000);
};

const productText = (order) => {
  const item = order.productSummary;
  if (!item) return '暂无产品摘要';
  return `${item.name || item.sku}${item.variant ? ` / ${item.variant}` : ''}${order.productCount > 1 ? ` 等 ${order.productCount} 款` : ''}`;
};

const ownerKey = (order) => order.ownerUserId || order.ownerName || '';
const ownerFilteredOrders = (orders = state.orders) => orders.filter((order) => !state.ownerFilter || ownerKey(order) === state.ownerFilter);

function renderOwnerFilter() {
  const select = $('#global-owner-filter');
  if (!select) return;
  const owners = new Map();
  state.orders.forEach((order) => {
    const key = ownerKey(order);
    if (key && !owners.has(key)) owners.set(key, order.ownerName || '未命名负责人');
  });
  if (state.ownerFilter && !owners.has(state.ownerFilter)) state.ownerFilter = '';
  select.innerHTML = '<option value="">全部负责人</option>' + [...owners]
    .sort((a, b) => a[1].localeCompare(b[1], 'zh-CN'))
    .map(([key, name]) => `<option value="${escapeHtml(key)}">${escapeHtml(name)}</option>`).join('');
  select.value = state.ownerFilter;
}

const filteredOrders = () => ownerFilteredOrders(state.orderViewItems === null ? state.orders : state.orderViewItems).filter((order) => {
  if (state.orderFilter !== 'all' && order.status !== state.orderFilter) return false;
  if (!state.search) return true;
  const keyword = state.search.toLowerCase();
  return [order.orderNo, order.customerName, order.ownerName, productText(order), order.productSummary?.sku]
    .filter(Boolean).some((value) => String(value).toLowerCase().includes(keyword));
});

function renderAll() {
  renderOwnerFilter();
  renderDashboard();
  renderOrders();
  renderTasks();
  renderAdminOrders();
}

function renderDashboard() {
  const orders = ownerFilteredOrders();
  const purchase = orders.filter((order) => ['pending_purchase', 'purchasing'].includes(order.status));
  const payment = orders.filter((order) => order.status === 'pending_payment');
  const ready = orders.filter((order) => order.status === 'purchased');
  const shipped = orders.filter((order) => order.status === 'shipped');
  const urgent = orders.filter((order) => !['shipped', 'cancelled'].includes(order.status) && deadlineDays(order.deadline) <= 3);
  $('#metric-urgent').textContent = urgent.length;
  $('#metric-purchase').textContent = purchase.length;
  $('#metric-payment').textContent = payment.length;
  $('#metric-ready').textContent = ready.length;
  $('#metric-shipped').textContent = shipped.length;
  $('#route-created').textContent = orders.length;
  $('#route-purchase').textContent = purchase.length;
  $('#route-payment').textContent = payment.length;
  $('#route-ready').textContent = ready.length;
  $('#route-shipped').textContent = shipped.length;
  $('#route-summary').textContent = orders.length
    ? `${orders.length} 张确认单正在协作，${urgent.length} 张接近出货期限。`
    : '暂无订单，创建第一张客户确认单开始协作。';

  const priority = [...orders]
    .filter((order) => !['shipped', 'cancelled'].includes(order.status))
    .sort((a, b) => deadlineDays(a.deadline) - deadlineDays(b.deadline)).slice(0, 5);
  $('#priority-list').innerHTML = priority.length ? priority.map((order) => {
    const date = String(order.deadline || '').slice(0, 10);
    const days = deadlineDays(order.deadline);
    return `<article class="priority-item" data-order-id="${order.id}">
      <div class="priority-date"><strong>${days < 0 ? `逾期${Math.abs(days)}天` : `${days}天`}</strong><span>${escapeHtml(date || '未定日期')}</span></div>
      <div class="priority-main"><strong>${escapeHtml(order.customerName)}</strong><span>${escapeHtml(productText(order))}</span></div>
      <span class="priority-amount">${escapeHtml(order.currency || 'USD')} ${money(order.totalAmount)}</span>
      ${statusPill(order.status)}
    </article>`;
  }).join('') : emptyMarkup('没有待处理订单');

  $('#latest-list').innerHTML = orders.slice(0, 6).map((order) => `<article class="latest-item" data-order-id="${order.id}">
    <div><strong>${escapeHtml(order.orderNo)}</strong>${statusPill(order.status)}</div>
    <p>${escapeHtml(order.customerName)}</p><small>${escapeHtml(productText(order))}</small>
  </article>`).join('') || emptyMarkup('暂无最近订单');
}

const statusPill = (status) => {
  const meta = statusMeta[status] || { label: status, className: '' };
  return `<span class="status-pill ${meta.className}">${escapeHtml(meta.label)}</span>`;
};

const emptyMarkup = (message) => `<div class="empty-state"><strong>${escapeHtml(message)}</strong><span>新的业务数据会显示在这里。</span></div>`;

function renderOrders() {
  const orders = filteredOrders();
  $('#order-count').textContent = `${orders.length} 条订单`;
  $('#orders-empty').classList.toggle('is-hidden', orders.length > 0);
  $('#orders-body').innerHTML = orders.map((order) => `<tr data-order-id="${order.id}">
    <td><div class="order-cell"><strong>${escapeHtml(order.orderNo)}</strong><span>${escapeHtml(order.customerName)}</span></div></td>
    <td><div class="product-summary">${escapeHtml(productText(order))}</div><span class="muted">${escapeHtml(order.productSummary?.sku || '')}</span></td>
    <td class="money">${escapeHtml(order.currency || 'USD')} ${money(order.totalAmount)}</td>
    <td class="money">CNY ${money(order.purchaseTotal)}</td>
    <td>${escapeHtml(String(order.deadline || '').slice(0, 10) || '待确认')}</td>
    <td>${escapeHtml(order.ownerName || '—')}</td><td>${statusPill(order.status)}</td><td><button class="row-action" aria-label="查看订单">→</button></td>
  </tr>`).join('');
}

function renderTasks() {
  const orders = ownerFilteredOrders();
  const purchase = orders.filter((order) => ['pending_purchase', 'purchasing'].includes(order.status));
  const payment = orders.filter((order) => order.status === 'pending_payment');
  const shipping = orders.filter((order) => order.status === 'purchased');
  const relevantCount = state.user?.role === 'logistics' ? shipping.length
    : state.user?.role === 'purchase' ? purchase.length : purchase.length + payment.length + shipping.length;
  $('#task-badge').textContent = relevantCount;
  $('#purchase-count').textContent = purchase.length;
  $('#payment-count').textContent = payment.length;
  $('#shipping-count').textContent = shipping.length;
  $('#purchase-tasks').innerHTML = taskCards(purchase, 'purchase');
  $('#payment-tasks').innerHTML = taskCards(payment, 'payment');
  $('#shipping-tasks').innerHTML = taskCards(shipping, 'shipping');
}

function renderAdminOrders() {
  const body = $('#admin-orders-body');
  if (!body || !state.user?.isDingAdmin) return;
  body.innerHTML = ownerFilteredOrders().map((order) => `<tr data-order-id="${order.id}">
    <td><div class="order-cell"><strong>${escapeHtml(order.orderNo)}</strong><span>${escapeHtml(String(order.deadline || '').slice(0, 10))}</span></div></td>
    <td>${escapeHtml(order.customerName)}</td><td>${order.productCount || 0} 款</td>
    <td class="money">${escapeHtml(order.currency || 'USD')} ${money(order.totalAmount)}</td><td>${statusPill(order.status)}</td>
    <td><div class="admin-actions"><button type="button" data-admin-edit="${order.id}">编辑</button><button type="button" class="danger-link" data-admin-delete="${order.id}">删除</button></div></td>
  </tr>`).join('') || '<tr><td colspan="6">暂无订单数据</td></tr>';
}

const taskCardMeta = {
  purchase: { className: '', footer: '等待采购确认', empty: '没有采购任务' },
  payment: { className: 'payment', footer: '等待财务确认付款', empty: '没有待付款订单' },
  shipping: { className: 'shipping', footer: '等待物流确认', empty: '没有待发货订单' }
};

const taskCards = (orders, kind) => {
  const meta = taskCardMeta[kind] || taskCardMeta.purchase;
  return orders.length ? orders.map((order) => `<article class="task-card ${meta.className}" data-order-id="${order.id}">
  <div><strong>${escapeHtml(order.orderNo)}</strong>${statusPill(order.status)}</div>
  <h4>${escapeHtml(order.customerName)}</h4><p>${escapeHtml(productText(order))}</p>
  <footer><span>${meta.footer}</span><span>${escapeHtml(String(order.deadline || '').slice(0, 10))}</span></footer>
</article>`).join('') : emptyMarkup(meta.empty);
};

function switchView(view) {
  if (view === 'admin' && !state.user?.isDingAdmin) return;
  if (view === 'performance' && !['sales', 'admin'].includes(state.user?.role)) return;
  state.currentView = view;
  $$('.nav-item').forEach((button) => button.classList.toggle('active', button.dataset.view === view));
  $$('.view').forEach((section) => section.classList.toggle('active', section.id === `view-${view}`));
  const titles = {
    dashboard: ['OPERATIONS BOARD', '今日协作总览'], orders: ['ORDER MANIFEST', '订单中心'],
    performance: ['COMMISSION LEDGER', '月度业绩'], tasks: ['ACTION QUEUE', '我的待办'],
    expenses: ['EXPENSE CLAIMS', state.user?.isDingAdmin ? '费用报销审批' : '我的费用报销'],
    admin: ['SUPER ADMINISTRATION', '超级管理后台']
  };
  $('#view-kicker').textContent = titles[view][0];
  $('#view-title').textContent = titles[view][1];
  if (view === 'admin') loadMembers();
  if (view === 'performance') {
    loadPerformance();
    if (state.user?.role === 'admin') loadPerformanceEmployees();
  }
  if (view === 'expenses') loadExpenses();
}

async function loadPerformanceEmployees() {
  try {
    const result = await api('/api/performance/employees');
    state.performanceEmployees = result.items || [];
    $('#performance-export-owner').innerHTML = state.performanceEmployees.map((item) =>
      `<option value="${item.id}" ${item.id === state.user.id ? 'selected' : ''}>${escapeHtml(item.name)}${item.title ? ` · ${escapeHtml(item.title)}` : ''}</option>`
    ).join('');
  } catch (error) { toast(`员工列表读取失败：${error.message}`); }
}

async function exportPerformanceExcel() {
  const month = $('#performance-export-month').value;
  if (!month) return toast('请选择导出月份');
  const params = new URLSearchParams({ month });
  if (state.user?.role === 'admin' && $('#performance-export-owner').value) {
    params.set('ownerUserId', $('#performance-export-owner').value);
  }
  const button = $('#performance-export-button');
  button.disabled = true;
  button.textContent = '正在生成…';
  try {
    const response = await fetch(`/api/performance/export.xlsx?${params}`, {
      headers: { Authorization: `Bearer ${state.token}` }
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error?.message || `导出失败（${response.status}）`);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const fileName = match ? decodeURIComponent(match[1]) : `员工业绩_${month}.xlsx`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    toast('Excel 业绩表已导出');
  } catch (error) { toast(`业绩导出失败：${error.message}`); }
  finally { button.disabled = false; button.textContent = '导出 Excel'; }
}

async function exportOrderExcel() {
  const order = state.currentDetail;
  if (!order || state.busy) return;
  setBusy(true);
  try {
    const response = await fetch(`/api/orders/${encodeURIComponent(order.id)}/export.xlsx`, {
      headers: { Authorization: `Bearer ${state.token}` }
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error?.message || `导出失败（${response.status}）`);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const fileName = match ? decodeURIComponent(match[1]) : `${order.orderNo || '订单'}_确认单.xlsx`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    toast('订单确认单已导出');
  } catch (error) { toast(`订单导出失败：${error.message}`); }
  finally { setBusy(false); }
}

async function openDetail(id) {
  state.purchaseAttachments = [];
  $('#detail-drawer').classList.add('open');
  $('#detail-order-no').textContent = '读取中…';
  $('#detail-content').innerHTML = '<div class="loading-line"></div>';
  $('#detail-actions').innerHTML = '';
  if (previewMode) {
    const order = state.orders.find((item) => item.id === id);
    state.currentDetail = {
      ...order,
      shippingAddress: `${order.destination} · 客户指定仓库`, note: '外箱需加贴客户条码，装柜前提供整批照片。',
      paymentAttachments: [], shipment: null,
      products: [
        { id: 'preview-product-1', sku: order.productSummary.sku, name: order.productSummary.name, variant: order.productSummary.variant, unitsPerCarton: 20, cartons: 10, weight: 128, volume: 1.4, quantity: 200, totalPrice: order.totalAmount, purchaseStatus: order.status === 'purchasing' ? 'pending' : 'completed', images: [] },
        { id: 'preview-product-2', sku: `${order.productSummary.sku}-B`, name: order.productSummary.name, variant: '雾灰色', unitsPerCarton: 20, cartons: 6, weight: 76, volume: .8, quantity: 120, totalPrice: order.totalAmount * .45, purchaseStatus: 'completed', images: [] }
      ]
    };
    renderDetail();
    return;
  }
  try {
    const result = await api(`/api/orders/${encodeURIComponent(id)}`);
    state.currentDetail = result.order;
    renderDetail();
  } catch (error) {
    $('#detail-content').innerHTML = emptyMarkup(`订单读取失败：${error.message}`);
  }
}

function closeDetail() {
  $('#detail-drawer').classList.remove('open');
  state.currentDetail = null;
}

const isImageAttachment = (file) => /^image\//i.test(file.fileType || '');

const attachmentGallery = (items = [], group = '凭证') => items.length
  ? `<div class="attachment-list">${attachmentGalleryTiles(items, group)}</div>`
  : '<div class="admin-image-empty">暂无凭证</div>';

const attachmentGalleryTiles = (items = [], group = '凭证') => items.map((file) => {
  const name = file.fileName || '凭证';
  const safeName = escapeHtml(name);
  if (isImageAttachment(file) && file.url) {
    const url = escapeHtml(file.url);
    return `<button type="button" class="attachment-preview image" data-image-gallery="${escapeHtml(group)}" data-image-src="${url}" data-image-name="${safeName}">
      <img src="${url}" alt="${safeName}" />
      <small>${safeName}</small>
      <em class="attachment-zoom">点击预览</em>
    </button>`;
  }
  const url = escapeHtml(file.url || '#');
  return `<a class="attachment-preview" href="${url}" target="_blank" rel="noreferrer">
    <span class="file-chip-icon">FILE</span>
    <small>${safeName}</small>
  </a>`;
}).join('');

const canSupplementOrderAttachments = (order) =>
  Boolean(order && (state.user?.isDingAdmin || state.user?.role === 'admin' || order.ownerUserId === state.user?.id));

const supplementUpload = (order, category, label) => canSupplementOrderAttachments(order)
  && ((category === 'payment' ? order.paymentAttachments : order.purchaseAttachments) || []).length < 9
    ? `<label class="supplement-upload"><input type="file" accept="image/*" multiple data-order-attachment-files="${category}" />＋ 补交${label}</label>`
    : '';

function renderDetail() {
  const order = state.currentDetail;
  $('#detail-order-no').textContent = order.orderNo;
  const meta = statusMeta[order.status] || { label: order.status, className: '' };
  $('#detail-status').className = `status-pill ${meta.className}`;
  $('#detail-status').textContent = meta.label;
  const productGoodsSum = (order.products || []).reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0), 0);
  const goodsFromReceived = order.receivedUsd !== null && order.receivedUsd !== undefined;
  const goodsNote = goodsFromReceived
    ? `实收美金 $${money(order.receivedUsd)} − 买家支付运费 ${escapeHtml(order.currency || 'USD')} ${money(order.freight)}`
    : '产品明细合计（未填实收美金）';
  const goodsDiff = goodsFromReceived && Math.abs(productGoodsSum - Number(order.goodsTotal || 0)) > 0.005 && Math.abs(productGoodsSum - (Number(order.receivedUsd || 0) - Number(order.freight || 0))) > 0.005;
  const productMarkup = (order.products || []).map((product) => {
    const images = product.images || [];
    const gallery = images.map((image) => `<div><a href="${escapeHtml(image.url || '#')}" target="_blank" rel="noreferrer"><img src="${escapeHtml(image.url || '')}" alt="${escapeHtml(image.fileName || '产品图片')}" /></a><a href="${escapeHtml(image.url || '#')}" download="${escapeHtml(image.fileName || '产品图片')}">下载</a></div>`).join('');
    return `<article class="detail-product">
    <div class="detail-product-image">${images[0]?.url ? `<a href="${escapeHtml(images[0].url)}" target="_blank" rel="noreferrer"><img src="${escapeHtml(images[0].url)}" alt="" /></a>` : escapeHtml(product.sku?.slice(0, 5) || '产品')}</div>
    <div><h5>${escapeHtml(product.name)} / ${escapeHtml(product.variant)}</h5><p>${product.sku ? `${escapeHtml(product.sku)} · ` : ''}${product.quantity} 件 · ${product.cartons} 箱<br />${product.weight} kg · ${product.volume} m³ · 装箱 ${product.unitsPerCarton} 件</p>${gallery ? `<div class="product-image-gallery">${gallery}</div>` : ''}</div>
    <aside><strong>USD ${money(product.totalPrice)}</strong>${product.purchaseStatus === 'completed'
      ? `<span class="status-pill purchased">已采购</span><small class="purchase-cost-saved">成本 CNY ${money(product.purchaseCost)}</small>`
      : canPurchase() ? `<label class="purchase-cost-field"><span>实际采购成本 CNY</span><input data-purchase-cost="${product.id}" type="number" min="0" step="0.01" value="${Number(product.purchaseCost || 0)}" /></label><button data-purchase-product="${product.id}">保存成本并确认</button>` : '<span class="status-pill">待采购</span>'}</aside>
  </article>`;
  }).join('');
  const purchaseStage = ['pending_purchase', 'purchasing'].includes(order.status);
  const purchaseAllCompleted = (order.products || []).length > 0
    && (order.products || []).every((item) => item.purchaseStatus === 'completed');
  const showWholePurchase = canPurchase() && purchaseStage;
  const showAttachments = canSupplementOrderAttachments(order)
    || (order.paymentAttachments || []).length
    || (order.purchaseAttachments || []).length
    || (order.shipment?.attachments || []).length;
  const purchaseTotalValue = order.purchaseTotal == null ? '' : Number(order.purchaseTotal);
  $('#detail-content').innerHTML = `
    <div class="detail-summary cols-4"><div><span>客户</span><strong>${escapeHtml(order.customerName)}</strong></div><div><span>货值</span><strong>${escapeHtml(order.currency)} ${money(order.goodsTotal)}</strong></div><div><span>订单总额</span><strong>${escapeHtml(order.currency)} ${money(order.totalAmount)}</strong></div><div><span>最晚出货</span><strong>${escapeHtml(String(order.deadline).slice(0,10))}</strong></div></div>
    <p class="detail-hint">货值口径：${goodsNote}${goodsDiff ? `；产品明细合计为 ${escapeHtml(order.currency)} ${money(productGoodsSum)}，以货值为准` : ''}</p>
    <section class="detail-block"><h4>产品明细 · ${order.products?.length || 0} 款</h4>${productMarkup || emptyMarkup('暂无产品')}</section>
    ${showWholePurchase ? `<section class="whole-purchase-card">
      <div><span class="whole-purchase-kicker">WHOLE ORDER COST</span><h4>整单快速确认</h4><p>填写一笔总采购成本并保存，即可一次确认所有剩余款式；保存只记录成本，订单仍需再点「完成采购，提交待付款」才流转。</p></div>
      <label><span>整单总采购成本</span><div><b>CNY</b><input id="whole-purchase-total" type="number" min="0" step="0.01" value="${purchaseTotalValue}" placeholder="0.00" /></div></label>
      <label><span>采购运费（选填，未填视为已含成本）</span><div><b>CNY</b><input id="purchase-freight" type="number" min="0" step="0.01" value="${Number(order.purchaseFreight || 0) || ''}" placeholder="0.00" /></div></label>
      <label class="upload-zone purchase-upload"><input id="purchase-files" type="file" accept="image/*" multiple /><span>＋ 上传采购凭证图片 / 采购单</span><small>图片附件，最多 9 张</small></label>
      <div id="purchase-previews" class="file-previews">${previewMarkup(state.purchaseAttachments, 'purchase')}</div>
    </section>` : ''}
    <section class="detail-block"><h4>财务结算</h4><div class="detail-note">货值：${escapeHtml(order.currency || 'USD')} ${money(order.goodsTotal)}（${goodsNote}）<br />总采购成本：CNY ${money(order.purchaseTotal)}<br />采购运费：CNY ${money(order.purchaseFreight)}（空白按 0）<br />买家支付运费：${escapeHtml(order.currency || 'USD')} ${money(order.freight)}<br />我方支出运费：CNY ${money(order.shippingFreight)}<br />实收美金：${order.receivedUsd == null ? '未填写（按订单总额入账）' : `$${money(order.receivedUsd)}`}<br />结算汇率：${order.exchangeRate || '按订单日期自动换算'} · ${order.isCompleted ? '已完结' : '未完结'}</div></section>
    <section class="detail-block"><h4>交付信息</h4><div class="detail-note">${escapeHtml(order.shippingAddress || order.destination || '尚未填写交付地址')}${order.tradeTerm ? `<br />贸易条款：${escapeHtml(order.tradeTerm)}${tradeTerms[order.tradeTerm] ? ` · ${escapeHtml(tradeTerms[order.tradeTerm].name)}` : ''}` : ''}<br />${escapeHtml(order.note || '无补充说明')}</div></section>
    ${order.shipment ? `<section class="detail-block"><h4>物流记录${order.status === 'purchased' ? '（已保存，待确认发货）' : ''}</h4><div class="detail-note">${escapeHtml(order.shipment.logisticsCompany)} · ${escapeHtml(order.shipment.trackingNo)}<br />${escapeHtml(String(order.shipment.shippedOn).slice(0,10))} → ${escapeHtml(String(order.shipment.estimatedArrivalOn).slice(0,10))}${order.status === 'purchased' ? '<br />物流与运费已保存，订单仍是待发货；确认无误后请点击「确认发货」。' : ''}</div></section>` : ''}
    ${showAttachments ? `<section class="detail-block attachment-block"><h4>关联凭证</h4>
      <div class="attachment-columns">
        <div class="attachment-group" data-attachment-category="payment">
          <div class="attachment-group-head"><div><strong>收款截图</strong><span>客户付款到账截图 · ${(order.paymentAttachments || []).length} 张</span></div>${supplementUpload(order, 'payment', '收款截图')}</div>
          ${attachmentGallery(order.paymentAttachments || [], '收款截图')}
        </div>
        <div class="attachment-group" data-attachment-category="purchase">
          <div class="attachment-group-head"><div><strong>采购凭证图片 / 采购单</strong><span>采购下单凭证与货物单号 · ${(order.purchaseAttachments || []).length} 张</span></div>${supplementUpload(order, 'purchase', '采购凭证图片 / 采购单')}</div>
          ${attachmentGallery(order.purchaseAttachments || [], '采购凭证图片 / 采购单')}
        </div>
      </div>
      ${(order.shipment?.attachments || []).length ? `<div class="attachment-group" data-attachment-category="logistics"><div class="attachment-group-head"><div><strong>物流凭证</strong><span>发货与运单资料 · ${(order.shipment?.attachments || []).length} 张</span></div></div>${attachmentGallery(order.shipment.attachments || [], '物流凭证')}</div>` : ''}
    </section>` : ''}`;

  const actions = [];
  if (canEditOrder(order)) actions.push('<button class="button secondary" data-admin-edit-detail>编辑订单</button>');
  if (state.user?.isDingAdmin) {
    if (order.shipment) {
      actions.push('<button class="button secondary" data-admin-edit-shipment>修改物流</button>');
      actions.push('<button class="button secondary" data-admin-delete-shipment>删除物流</button>');
    }
    actions.push('<button class="button danger" data-admin-delete-detail>删除订单</button>');
  }
  actions.push('<button class="button secondary" data-export-order>导出订单表单</button>');
  if (canPurchase() && purchaseStage) actions.push('<button class="button secondary" data-purchase-all>保存整单总采购成本</button>');
  if (canPurchase() && purchaseStage && purchaseAllCompleted) actions.push('<button class="button primary" data-submit-purchase>完成采购，提交待付款</button>');
  if ((state.user?.role === 'admin' || state.user?.isDingAdmin) && order.status === 'pending_payment') actions.push('<button class="button primary" data-confirm-payment>确认付款</button>');
  if (canShip() && order.status === 'purchased') {
    actions.push('<button class="button primary" data-open-shipment>填写物流与运费</button>');
    // 运费/物流数据保存后订单仍是待发货，需要再点一次确认才进入已发货。
    if (order.shipment) actions.push('<button class="button primary" data-confirm-shipment>确认发货（进入已发货）</button>');
  }
  // 已发货但未填我方支出运费时，物流与管理员可直接补录（人民币 CNY）。
  if (canShip() && order.status === 'shipped' && Number(order.shippingFreight || 0) <= 0) {
    actions.push('<button class="button primary" data-open-shipment>补录我方支出运费（CNY）</button>');
  }
  $('#detail-actions').innerHTML = actions.join('');
}

const canPurchase = () => ['purchase', 'admin'].includes(state.user?.role);
const canShip = () => ['logistics', 'admin'].includes(state.user?.role);
// 业务员可以重新编辑自己负责的订单；其余整单编辑仍限钉钉超级管理员。
const canEditOrder = (order) => Boolean(order) && (
  Boolean(state.user?.isDingAdmin)
  || (state.user?.role === 'sales' && order.ownerUserId === state.user?.id)
);

async function completePurchase(productIds) {
  if (!state.currentDetail || state.busy) return;
  let body;
  if (productIds) {
    const pending = (state.currentDetail.products || []).filter((item) =>
      item.purchaseStatus !== 'completed' && productIds.includes(item.id)
    );
    const products = pending.map((item) => {
      const input = document.querySelector(`[data-purchase-cost="${item.id}"]`);
      return { id: item.id, purchaseCost: input ? input.value : '' };
    });
    if (!products.length) return toast('没有可确认的待采购产品');
    if (products.some((item) => item.purchaseCost === '' || !Number.isFinite(Number(item.purchaseCost)) || Number(item.purchaseCost) < 0)) {
      return toast('请填写该产品的实际采购成本');
    }
    body = { products: products.map((item) => ({ id: item.id, purchaseCost: Number(item.purchaseCost) })) };
  } else {
    const input = $('#whole-purchase-total');
    const purchaseTotal = input ? input.value : '';
    if (purchaseTotal === '' || !Number.isFinite(Number(purchaseTotal)) || Number(purchaseTotal) < 0) {
      return toast('请填写整单采购总成本');
    }
    body = { purchaseTotal: Number(purchaseTotal) };
  }
  const purchaseFreightInput = $('#purchase-freight');
  const purchaseFreightText = purchaseFreightInput ? purchaseFreightInput.value : '';
  if (purchaseFreightText !== '' && (!Number.isFinite(Number(purchaseFreightText)) || Number(purchaseFreightText) < 0)) {
    return toast('采购运费必须是大于或等于 0 的数字');
  }
  if (purchaseFreightText !== '') body.purchaseFreight = Number(purchaseFreightText);
  if (state.purchaseAttachments.length) body.purchaseAttachments = state.purchaseAttachments;
  setBusy(true);
  try {
    const result = await api(`/api/orders/${state.currentDetail.id}/purchase-complete`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
    state.currentDetail = result.order;
    state.purchaseAttachments = [];
    replaceOrder(result.order);
    renderDetail();
    toast(productIds ? '该款采购成本已保存（订单仍停留在采购阶段）' : '整单采购总成本已保存；确认无误后请点击「完成采购，提交待付款」');
  } catch (error) {
    toast(`采购确认失败：${error.message}`);
  } finally { setBusy(false); }
}

// 采购完成后显式提交，“填写成本”本身不再推动订单阶段。
async function submitPurchase() {
  if (!state.currentDetail || state.busy) return;
  const confirmed = await askConfirm({
    title: '提交待付款',
    message: `确认订单 ${state.currentDetail.orderNo} 全部款式都已录入采购成本，并提交待付款？提交后进入财务确认付款环节。`,
    confirmText: '提交待付款',
    danger: false
  });
  if (!confirmed) return;
  setBusy(true);
  try {
    const result = await api(`/api/orders/${state.currentDetail.id}/purchase-submit`, { method: 'POST' });
    state.currentDetail = result.order;
    replaceOrder(result.order);
    renderDetail();
    toast('采购已完成，订单进入待付款');
  } catch (error) { toast(`提交待付款失败：${error.message}`); }
  finally { setBusy(false); }
}

// 物流数据/运费保存后仍需二次确认，订单才从待发货进入已发货。
async function submitShipmentConfirm() {
  if (!state.currentDetail || state.busy) return;
  const confirmed = await askConfirm({
    title: '确认发货',
    message: `确认订单 ${state.currentDetail.orderNo} 已按已保存的物流信息发货？订单将进入已发货阶段。`,
    confirmText: '确认发货',
    danger: false
  });
  if (!confirmed) return;
  setBusy(true);
  try {
    const result = await api(`/api/orders/${state.currentDetail.id}/shipment-confirm`, { method: 'POST' });
    state.currentDetail = result.order;
    replaceOrder(result.order);
    renderDetail();
    toast('订单已发货');
  } catch (error) { toast(`确认发货失败：${error.message}`); }
  finally { setBusy(false); }
}

async function confirmPayment() {
  if (!state.currentDetail || state.busy) return;
  setBusy(true);
  try {
    const result = await api(`/api/orders/${state.currentDetail.id}/payment-confirm`, { method: 'POST' });
    state.currentDetail = result.order;
    replaceOrder(result.order);
    renderDetail();
    toast('付款已确认，订单进入待发货');
  } catch (error) {
    toast(`确认付款失败：${error.message}`);
  } finally { setBusy(false); }
}

function replaceOrder(order) {
  const index = state.orders.findIndex((item) => item.id === order.id);
  const summary = order.products?.[0] || null;
  const listOrder = { ...order, productSummary: summary, productCount: order.products?.length || 0 };
  if (index >= 0) state.orders.splice(index, 1, listOrder); else state.orders.unshift(listOrder);
  renderAll();
}

function openCreate() {
  state.productDrafts = [emptyProduct()];
  state.paymentAttachments = [];
  $('#create-form').reset();
  $('#create-form [name="deadline"]').value = today();
  renderProductEditors();
  renderPaymentPreviews();
  $('#create-dialog').showModal();
}

function renderProductEditors() {
  $('#product-editors').innerHTML = state.productDrafts.map((product, index) => `<article class="product-editor" data-index="${index}">
    <div class="product-editor-head"><strong>产品 ${index + 1}${product.name ? ` · ${escapeHtml(product.name)}` : ''}</strong>${state.productDrafts.length > 1 ? `<button type="button" class="remove-product" data-remove-product="${index}">删除此产品</button>` : ''}</div>
    <div class="product-fields">
      ${productInput(index, 'sku', '货号（选填）', product.sku, 'wide')}${productInput(index, 'name', '名称 *', product.name, 'wide')}${productInput(index, 'variant', '颜色 / 款式 *', product.variant, 'wide')}
      ${productInput(index, 'unitsPerCarton', '装箱数', product.unitsPerCarton, '', 'number')}${productInput(index, 'cartons', '箱数', product.cartons, '', 'number')}${productInput(index, 'weight', '重量 kg', product.weight, '', 'number', '0.01')}${productInput(index, 'volume', '体积 m³', product.volume, '', 'number', '0.001')}${productInput(index, 'quantity', '数量 *', product.quantity, '', 'number')}${productInput(index, 'unitPrice', '单价 USD', product.unitPrice, '', 'number', '0.01')}
      ${productInput(index, 'purchaseCost', '采购成本 CNY', product.purchaseCost, '', 'number', '0.01')}
      <label class="product-upload"><span>产品图片 · ${product.images.length}/9</span><input type="file" accept="image/*" multiple data-product-files="${index}" />${miniPreviews(product.images)}</label>
    </div></article>`).join('');
}

const productInput = (index, field, label, value, className = '', type = 'text', step = '1') => `<label class="${className}"><span>${label}</span><input data-product-index="${index}" data-product-field="${field}" type="${type}" ${type === 'number' ? `min="0" step="${step}"` : ''} value="${escapeHtml(value)}" /></label>`;
const miniPreviews = (items) => items.length ? `<div class="mini-previews">${items.slice(0, 5).map((file) => `<img src="${escapeHtml(file.url)}" alt="" />`).join('')}</div>` : '';

async function uploadProductFiles(index, files) {
  const remaining = 9 - state.productDrafts[index].images.length;
  if (remaining <= 0) return toast('每个产品最多 9 张图片');
  setBusy(true, '正在上传产品图片…');
  try {
    for (const file of [...files].slice(0, remaining)) state.productDrafts[index].images.push(await uploadImage(file, 'product'));
    renderProductEditors();
    toast('产品图片已上传至 OSS');
  } catch (error) { toast(`图片上传失败：${error.message}`); }
  finally { setBusy(false); }
}

async function uploadAttachments(files, category) {
  const target = category === 'payment' ? state.paymentAttachments
    : category === 'purchase' ? state.purchaseAttachments
      : category === 'expense' ? state.expenseAttachments : state.shipmentAttachments;
  const selected = [...files];
  const supportedImage = (file) => /^image\/(jpeg|png|webp|gif|heic|heif)$/i.test(file.type || '')
    || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(file.name || '');
  const accepted = category === 'expense' ? selected : selected.filter(supportedImage);
  if (!accepted.length) return toast('请选择 JPG、PNG、WebP、GIF 或 HEIC 图片');
  if (accepted.length !== selected.length) toast(`已忽略 ${selected.length - accepted.length} 个非图片文件`);
  const remaining = 9 - target.length;
  if (remaining <= 0) return toast('最多上传 9 张图片');
  setBusy(true, '正在上传图片…');
  try {
    for (const file of accepted.slice(0, remaining)) {
      target.push(category === 'expense' ? await uploadExpenseFile(file) : await uploadImage(file, category));
    }
    if (category === 'payment') renderPaymentPreviews();
    else if (category === 'purchase') renderPurchasePreviews();
    else if (category === 'expense') renderExpensePreviews();
    else renderShipmentPreviews();
    toast('图片已上传至 OSS');
  } catch (error) { toast(`图片上传失败：${error.message}`); }
  finally { setBusy(false); }
}

async function uploadOrderSupplementAttachments(category, files) {
  if (!state.currentDetail || state.busy) return;
  const current = category === 'payment'
    ? (state.currentDetail.paymentAttachments || [])
    : (state.currentDetail.purchaseAttachments || []);
  const remaining = 9 - current.length;
  if (remaining <= 0) return toast('该类凭证最多 9 张图片');
  setBusy(true, '正在补交图片凭证…');
  try {
    const attachments = [];
    for (const file of [...files].slice(0, remaining)) attachments.push(await uploadImage(file, category));
    const result = await api(`/api/orders/${state.currentDetail.id}/attachments`, {
      method: 'POST',
      body: JSON.stringify({ category, attachments })
    });
    state.currentDetail = result.order;
    replaceOrder(result.order);
    renderDetail();
    toast('图片凭证已补交');
  } catch (error) { toast(`补交失败：${error.message}`); }
  finally { setBusy(false); }
}

const previewMarkup = (items, category) => items.map((file, index) => {
  const image = isImageAttachment(file);
  return `<div class="file-chip">${image ? `<img src="${escapeHtml(file.url)}" alt="" />` : `<span class="file-chip-icon">FILE</span>`}<small>${escapeHtml(file.fileName || '附件')}</small><button type="button" data-remove-file="${category}:${index}">×</button></div>`;
}).join('');
const renderPaymentPreviews = () => { $('#payment-previews').innerHTML = previewMarkup(state.paymentAttachments, 'payment'); };
const renderPurchasePreviews = () => { const element = $('#purchase-previews'); if (element) element.innerHTML = previewMarkup(state.purchaseAttachments, 'purchase'); };
const renderShipmentPreviews = () => { $('#shipment-previews').innerHTML = previewMarkup(state.shipmentAttachments, 'shipment'); };
const renderExpensePreviews = () => { $('#expense-previews').innerHTML = previewMarkup(state.expenseAttachments, 'expense'); };

function openExpenseDialog() {
  state.expenseAttachments = [];
  $('#expense-form').reset();
  $('#expense-form [name="incurredOn"]').value = today();
  renderExpensePreviews();
  $('#expense-dialog').showModal();
}

async function submitExpense(event) {
  event.preventDefault();
  if (state.busy) return;
  const form = new FormData(event.currentTarget);
  const data = {
    category: form.get('category'),
    incurredOn: form.get('incurredOn'),
    currency: form.get('currency'),
    amount: Number(form.get('amount')),
    description: String(form.get('description') || '').trim(),
    attachments: state.expenseAttachments
  };
  setBusy(true);
  $('#save-expense').textContent = '正在提交…';
  try {
    await api('/api/expenses', { method: 'POST', body: JSON.stringify(data) });
    $('#expense-dialog').close();
    await loadExpenses();
    toast('报销申请已提交给超级管理员');
  } catch (error) { toast(`报销申请提交失败：${error.message}`); }
  finally { $('#save-expense').textContent = '提交给超级管理员'; setBusy(false); }
}

async function loadExpenses() {
  $('#expense-list').innerHTML = '<div class="loading-line"></div>';
  try {
    const result = await api('/api/expenses');
    state.expenses = result.items || [];
    renderExpenses();
  } catch (error) {
    $('#expense-list').innerHTML = `<div class="empty-state"><strong>报销记录读取失败</strong><span>${escapeHtml(error.message)}</span></div>`;
  }
}

function renderExpenses() {
  const isSuperAdmin = Boolean(state.user?.isDingAdmin);
  $('#expense-view-title').textContent = isSuperAdmin ? '费用报销审批' : '我的费用报销';
  $('#expense-view-note').textContent = isSuperAdmin ? '待审批申请优先显示，可查阅全员报销记录。' : '申请将提交给钉钉超级管理员审批。';
  $('#expense-list').innerHTML = state.expenses.map((expense) => {
    const receipts = (expense.attachments || []).map((file) => {
      const image = /^image\//i.test(file.fileType || '');
      return `<a href="${escapeHtml(file.url || '#')}" target="_blank" rel="noreferrer">${image ? `<img src="${escapeHtml(file.url || '')}" alt="${escapeHtml(file.fileName)}" />` : '<span>FILE</span>'}<small>${escapeHtml(file.fileName)}</small></a>`;
    }).join('');
    const paymentProofs = (expense.paymentAttachments || []).map((file) =>
      `<a href="${escapeHtml(file.url || '#')}" target="_blank" rel="noreferrer"><img src="${escapeHtml(file.url || '')}" alt="${escapeHtml(file.fileName || '支付凭证')}" /><small>${escapeHtml(file.fileName || '支付凭证')}</small></a>`
    ).join('');
    const paymentSection = `
      <section class="expense-payment-proofs">
        <div><strong>报销支付凭证</strong>${isSuperAdmin && (expense.paymentAttachments || []).length < 9 ? `<label>＋ 上传图片<input type="file" accept="image/*" multiple data-expense-payment-files="${expense.id}" /></label>` : ''}</div>
        ${paymentProofs ? `<div class="expense-receipts-pc">${paymentProofs}</div>` : '<p>管理员尚未上传支付凭证</p>'}
      </section>`;
    const reimbursed = isSuperAdmin
      ? `<button class="reimbursed-mark ${expense.isReimbursed ? 'done' : ''}" data-expense-reimbursed="${expense.id}" type="button">${expense.isReimbursed ? '✓ 已报销' : '□ 未报销'} · 点击更新</button>`
      : `<p class="reimbursed-mark ${expense.isReimbursed ? 'done' : ''}">${expense.isReimbursed ? '✓ 已报销' : '未报销'}</p>`;
    const decision = isSuperAdmin && expense.status === 'pending' ? `<div class="expense-decision"><input data-expense-comment="${expense.id}" placeholder="审批意见（驳回时必填）" /><button class="button secondary" data-expense-decision="${expense.id}:rejected">驳回</button><button class="button primary" data-expense-decision="${expense.id}:approved">通过</button></div>` : '';
    return `<article class="expense-card-pc">
      <header><div><strong>${escapeHtml(isSuperAdmin ? expense.applicantName : expenseCategoryNames[expense.category] || expense.category)}</strong><span>${escapeHtml(expense.expenseNo)} · ${escapeHtml(String(expense.incurredOn).slice(0, 10))}</span></div><em class="expense-state ${escapeHtml(expense.status)}">${escapeHtml(expenseStatusNames[expense.status] || expense.status)}</em></header>
      <div class="expense-money"><small>${escapeHtml(expense.currency)}</small>${money(expense.amount)}</div>
      ${isSuperAdmin ? `<dl class="expense-person-fields"><div><dt>部门</dt><dd>${escapeHtml(expense.applicantDepartment || '未配置')}</dd></div><div><dt>姓名</dt><dd>${escapeHtml(expense.applicantName)}</dd></div><div><dt>职位</dt><dd>${escapeHtml(expense.applicantTitle || '未填写')}</dd></div><div><dt>报销类型</dt><dd>${escapeHtml(expenseCategoryNames[expense.category] || expense.category)}</dd></div></dl>` : ''}${reimbursed}<blockquote>${escapeHtml(expense.description)}</blockquote>
      ${receipts ? `<div class="expense-receipts-pc">${receipts}</div>` : ''}
      ${paymentSection}
      ${expense.status !== 'pending' ? `<footer>审批人：${escapeHtml(expense.reviewerName || '超级管理员')}${expense.reviewComment ? ` · ${escapeHtml(expense.reviewComment)}` : ''}</footer>` : decision}
    </article>`;
  }).join('') || '<div class="empty-state"><strong>暂无报销记录</strong><span>点击右上角提交第一笔申请。</span></div>';
}

async function uploadExpensePaymentProofs(id, files) {
  const expense = state.expenses.find((item) => item.id === id);
  if (!expense || !state.user?.isDingAdmin || !files.length) return;
  const remaining = 9 - (expense.paymentAttachments || []).length;
  if (remaining <= 0) return toast('支付凭证最多上传 9 张');
  setBusy(true, '正在上传报销支付凭证…');
  const attachments = [];
  try {
    for (const file of [...files].slice(0, remaining)) attachments.push(await uploadImage(file, 'expense'));
    await api(`/api/expenses/${id}/payment-attachments`, {
      method: 'POST', body: JSON.stringify({ attachments })
    });
    await loadExpenses();
    toast('支付凭证已上传，员工可以查看');
  } catch (error) { toast(`支付凭证上传失败：${error.message}`); }
  finally { setBusy(false); }
}

async function decideExpense(id, status) {
  const input = document.querySelector(`[data-expense-comment="${id}"]`);
  const comment = input?.value.trim() || '';
  if (status === 'rejected' && !comment) return toast('驳回时请填写审批意见');
  try {
    await api(`/api/expenses/${id}/decision`, { method: 'PATCH', body: JSON.stringify({ status, comment }) });
    await loadExpenses();
    toast(status === 'approved' ? '报销已通过' : '报销已驳回');
  } catch (error) { toast(`审批失败：${error.message}`); }
}

async function toggleExpenseReimbursed(id) {
  const expense = state.expenses.find((item) => item.id === id);
  if (!expense || !state.user?.isDingAdmin) return;
  try {
    await api(`/api/expenses/${id}/reimbursed`, {
      method: 'PATCH', body: JSON.stringify({ isReimbursed: !expense.isReimbursed })
    });
    await loadExpenses();
    toast(expense.isReimbursed ? '已改为未报销' : '已标记为已报销');
  } catch (error) { toast(`报销状态更新失败：${error.message}`); }
}

async function submitOrder(event) {
  event.preventDefault();
  if (state.busy) return;
  const form = new FormData(event.currentTarget);
  const invalid = state.productDrafts.some((item) => !item.name.trim() || !item.variant.trim() || Number(item.quantity) <= 0);
  if (invalid) return toast('请填写每款产品的名称、颜色款式和数量');
  const data = {
    customerName: form.get('customerName'), customerContact: form.get('customerContact') || null,
    shippingAddress: form.get('shippingAddress') || null, destination: form.get('destination') || null,
    deadline: form.get('deadline'), paymentMethod: form.get('paymentMethod'), tradeTerm: form.get('tradeTerm') || null, currency: 'USD',
    freight: Number(form.get('freight') || 0), receivedUsd: form.get('receivedUsd') ? Number(form.get('receivedUsd')) : null,
    note: form.get('note') || null,
    products: state.productDrafts.map((item) => ({ ...item,
      unitsPerCarton: Number(item.unitsPerCarton), cartons: Number(item.cartons), weight: Number(item.weight),
      volume: Number(item.volume), quantity: Number(item.quantity), unitPrice: Number(item.unitPrice), purchaseCost: Number(item.purchaseCost)
    })), paymentAttachments: state.paymentAttachments
  };
  setBusy(true);
  $('#save-order').textContent = '正在创建…';
  try {
    const result = await api('/api/orders', { method: 'POST', body: JSON.stringify(data) });
    replaceOrder(result.order);
    void loadLeaderboard();
    if (state.orderDateFrom || state.orderDateTo) void loadOrderView();
    $('#create-dialog').close();
    toast(`订单 ${result.order.orderNo} 已创建`);
  } catch (error) { toast(`创建失败：${error.message}`); }
  finally { $('#save-order').textContent = '创建并同步采购'; setBusy(false); }
}

async function importPiFile(file) {
  if (!file || state.busy) return;
  if (!/\.xlsx$/i.test(file.name)) return toast('请选择 .xlsx 格式的 PI 报价单');
  const formData = new FormData();
  formData.append('file', file);
  setBusy(true);
  $('#import-pi').textContent = '正在解析 PI…';
  try {
    const result = await api('/api/imports/pi', { method: 'POST', body: formData });
    const imported = result.imported;
    state.productDrafts = imported.products.map((product) => ({ ...emptyProduct(), ...product }));
    const form = $('#create-form');
    if (imported.customerName) form.elements.customerName.value = imported.customerName;
    if (imported.quotationDate && !form.elements.note.value.trim()) {
      form.elements.note.value = `PI 报价日期：${imported.quotationDate}；来源文件：${imported.fileName}`;
    }
    renderProductEditors();
    const warning = imported.warnings?.length ? `；${imported.warnings[0]}` : '';
    toast(`已从 PI 导入 ${imported.products.length} 款产品${warning}`);
  } catch (error) {
    toast(`PI 导入失败：${error.message}`);
  } finally {
    $('#import-pi').textContent = '⇧ 导入 PI 报价单';
    $('#pi-file-input').value = '';
    setBusy(false);
  }
}

// 待发货阶段打开弹窗时只保存物流与运费；已发货阶段走“修改物流”。
// 订单进入已发货必须落在「确认发货」上，填写运费本身不推进阶段。
function openShipment(mode = 'draft') {
  const order = state.currentDetail;
  if (!order) return;
  const editing = mode === 'edit';
  state.shipmentOrderId = order.id;
  state.shipmentMode = editing ? 'edit' : 'draft';
  state.shipmentAction = editing ? 'update' : 'dispatch';
  const shipment = order.shipment;
  state.shipmentAttachments = [...(shipment?.attachments || [])];
  $('#shipment-form').reset();
  $('#shipment-dialog .modal-head h3').textContent = editing
    ? '修改物流数据（可补录运费）'
    : (shipment ? '物流与运费（已保存，待确认发货）' : '填写物流与运费');
  $('#save-shipment').textContent = editing ? '保存物流修改' : '确认发货';
  $('#save-shipment-draft').classList.toggle('is-hidden', editing);
  const currentFreight = Number(order.shippingFreight || 0);
  // 我方支出运费按人民币录入；已发货也可在“修改物流”里补录或修正。
  $('#shipment-freight-field').classList.remove('is-hidden');
  $('#shipment-freight-label').textContent = editing
    ? '我方支出运费（CNY 人民币，可补录或修正）'
    : '我方支出运费（CNY 人民币，选填）';
  $('#shipment-form [name="shippingFreight"]').value = currentFreight > 0 ? String(currentFreight) : '';
  $('#shipment-form [name="shippedOn"]').value = shipment ? String(shipment.shippedOn).slice(0, 10) : today();
  if (shipment) {
    $('#shipment-form [name="logisticsCompany"]').value = shipment.logisticsCompany || '';
    $('#shipment-form [name="trackingNo"]').value = shipment.trackingNo || '';
    $('#shipment-form [name="estimatedArrivalOn"]').value = String(shipment.estimatedArrivalOn || '').slice(0, 10);
    $('#shipment-form [name="note"]').value = shipment.note || '';
  }
  renderShipmentPreviews();
  $('#shipment-dialog').showModal();
}

async function submitShipment(event) {
  event.preventDefault();
  if (state.busy) return;
  const form = new FormData(event.currentTarget);
  const data = Object.fromEntries(['logisticsCompany', 'trackingNo', 'shippedOn', 'estimatedArrivalOn', 'note'].map((key) => [key, form.get(key) || null]));
  const shippingFreightText = form.get('shippingFreight');
  if (shippingFreightText !== '' && shippingFreightText !== null) {
    const shippingFreight = Number(shippingFreightText);
    if (!Number.isFinite(shippingFreight) || shippingFreight < 0) return toast('我方支出运费必须是大于或等于 0 的人民币金额');
    data.shippingFreight = shippingFreight;
  }
  data.attachments = state.shipmentAttachments;
  if (!data.logisticsCompany || !data.trackingNo || !data.shippedOn || !data.estimatedArrivalOn) return toast('请完整填写物流公司、单号和日期');
  if (data.estimatedArrivalOn < data.shippedOn) return toast('预计到达不能早于发货日期');
  const orderId = state.shipmentOrderId;
  const editing = state.shipmentMode === 'edit';
  const dispatch = !editing && state.shipmentAction !== 'save';
  setBusy(true);
  $('#save-shipment').textContent = '正在保存…';
  try {
    if (editing) {
      const result = await api(`/api/orders/${orderId}/shipment`, { method: 'PUT', body: JSON.stringify(data) });
      state.currentDetail = result.order;
      replaceOrder(result.order);
      renderDetail();
      $('#shipment-dialog').close();
      toast('物流数据已更新');
    } else {
      // 先落库物流与运费（订单仍是待发货），需要发货时再显式确认。
      const saved = await api(`/api/orders/${orderId}/shipments`, { method: 'POST', body: JSON.stringify(data) });
      state.currentDetail = saved.order;
      replaceOrder(saved.order);
      if (dispatch) {
        const result = await api(`/api/orders/${orderId}/shipment-confirm`, { method: 'POST' });
        state.currentDetail = result.order;
        replaceOrder(result.order);
        $('#shipment-dialog').close();
        toast('物流已保存，订单已发货');
      } else {
        $('#shipment-dialog').close();
        toast('物流与运费已保存，订单仍为待发货；确认无误后点击「确认发货」');
      }
      renderDetail();
    }
  } catch (error) {
    toast(`${editing ? '物流修改失败' : (dispatch ? '发货失败' : '物流保存失败')}：${error.message}`);
  }
  finally {
    $('#save-shipment').textContent = editing ? '保存物流修改' : '确认发货';
    setBusy(false);
  }
}

async function openAdminOrderEditor(orderId) {
  try {
    let order = state.currentDetail?.id === orderId ? state.currentDetail : null;
    if (!order) order = (await api(`/api/orders/${encodeURIComponent(orderId)}`)).order;
    if (!canEditOrder(order)) return toast('只有订单负责人或超级管理员可以编辑该订单');
    const isSuperAdmin = Boolean(state.user?.isDingAdmin);
    if (isSuperAdmin && !state.members.length) await loadMembers();
    state.currentDetail = order;
    state.adminEditIsSuperAdmin = isSuperAdmin;
    state.adminEditProducts = (order.products || []).map((item) => ({ ...item }));
    state.adminPaymentAttachments = [...(order.paymentAttachments || [])];
    state.adminPurchaseAttachments = [...(order.purchaseAttachments || [])];
    const form = $('#admin-order-form');
    form.reset();
    ['customerName', 'customerContact', 'shippingAddress', 'destination', 'deadline', 'paymentMethod', 'tradeTerm', 'currency', 'freight', 'purchaseFreight', 'receivedUsd', 'exchangeRate', 'note'].forEach((field) => {
      const value = order[field];
      if (form.elements[field]) form.elements[field].value = field === 'deadline' ? String(value || '').slice(0, 10) : (value ?? '');
    });
    // 整单总采购成本只在与各款成本合计不一致时预填，留空即按产品明细自动汇总。
    const productCostSum = state.adminEditProducts.reduce((sum, item) => sum + Number(item.purchaseCost || 0), 0);
    const hasPurchaseTotalOverride = Math.abs(Number(order.purchaseTotal || 0) - productCostSum) > 0.005;
    if (form.elements.purchaseTotal) {
      form.elements.purchaseTotal.value = hasPurchaseTotalOverride ? String(Number(order.purchaseTotal)) : '';
    }
    const adminTradeTermNote = $('#admin-trade-term-note');
    if (adminTradeTermNote) adminTradeTermNote.textContent = tradeTermNoteText(order.tradeTerm);
    $('#admin-order-title').textContent = `编辑 ${order.orderNo}`;
    form.elements.isCompleted.checked = Boolean(order.isCompleted);
    // 超级管理员可改派负责人；业务员只能编辑自己负责的订单，负责人固定为本人。
    const ownerOptions = isSuperAdmin
      ? state.members.map((member) => ({ id: member.id, label: `${member.name} · ${roleNames[member.role] || member.role}` }))
      : [{ id: state.user.id, label: `${state.user.name} · ${roleNames[state.user.role] || state.user.role}` }];
    if (order.ownerUserId && !ownerOptions.some((item) => item.id === order.ownerUserId)) {
      ownerOptions.push({ id: order.ownerUserId, label: order.ownerName || order.ownerUserId });
    }
    $('#admin-order-owner').innerHTML = ownerOptions.map((item) =>
      `<option value="${escapeHtml(item.id)}" ${item.id === order.ownerUserId ? 'selected' : ''}>${escapeHtml(item.label)}</option>`
    ).join('');
    $$('#admin-order-form [data-super-admin-field]').forEach((element) => element.classList.toggle('is-hidden', !isSuperAdmin));
    syncAdminPurchaseTotalHint();
    renderAdminProductEditors();
    renderAdminAttachmentEditors();
    $('#admin-order-dialog').showModal();
  } catch (error) { toast(`无法打开编辑器：${error.message}`); }
}

// 提示各款采购成本合计，帮助填写整单总采购成本。
function syncAdminPurchaseTotalHint() {
  const hint = $('#admin-purchase-total-hint');
  if (!hint) return;
  const sum = state.adminEditProducts.reduce((total, item) => total + Number(item.purchaseCost || 0), 0);
  hint.textContent = `留空时按各款采购成本自动合计：CNY ${money(sum)}`;
}

function renderAdminProductEditors() {
  const isSuperAdmin = state.adminEditIsSuperAdmin;
  $('#admin-product-editors').innerHTML = state.adminEditProducts.map((product, index) => `<article class="admin-product-editor" data-admin-product-index="${index}">
    <div class="product-editor-head"><strong>产品 ${index + 1}${product.id ? '' : ' · 新增'}</strong>${state.adminEditProducts.length > 1 ? `<button type="button" class="remove-product" data-admin-remove-product="${index}">删除</button>` : ''}</div>
    <div class="admin-product-grid">
      ${adminProductInput(index, 'sku', '货号（选填）', product.sku, 'wide')}${adminProductInput(index, 'name', '名称 *', product.name, 'wide')}${adminProductInput(index, 'variant', '颜色 / 款式 *', product.variant, 'wide')}
      ${adminProductInput(index, 'unitsPerCarton', '装箱数', product.unitsPerCarton, '', 'number')}${adminProductInput(index, 'cartons', '箱数', product.cartons, '', 'number')}${adminProductInput(index, 'weight', '重量 kg', product.weight, '', 'number', '.001')}${adminProductInput(index, 'volume', '体积 m³', product.volume, '', 'number', '.0001')}${adminProductInput(index, 'quantity', '数量', product.quantity, '', 'number')}${adminProductInput(index, 'unitPrice', '单价', product.unitPrice, '', 'number', '.01')}
      ${isSuperAdmin ? adminProductInput(index, 'purchaseCost', '采购成本 CNY', product.purchaseCost, '', 'number', '.01') : ''}
      ${isSuperAdmin ? `<label><span>采购状态</span><select data-admin-product-index="${index}" data-admin-product-field="purchaseStatus"><option value="pending" ${product.purchaseStatus === 'pending' ? 'selected' : ''}>待采购</option><option value="completed" ${product.purchaseStatus === 'completed' ? 'selected' : ''}>已采购</option></select></label>` : `<div class="admin-product-cost-note"><span>采购成本 CNY ${money(product.purchaseCost)}</span><span>${product.purchaseStatus === 'completed' ? '已采购' : '待采购'}</span><small>采购成本与采购状态由采购/超级管理员维护</small></div>`}
      <div class="admin-product-images">
        <div class="admin-product-images-head"><span>产品图片 · ${(product.images || []).length}/9</span><label class="admin-image-upload"><input type="file" accept="image/*" multiple data-admin-product-files="${index}" />＋ 上传图片</label></div>
        ${adminImagePreviews(product.images || [], index)}
      </div>
    </div></article>`).join('');
}

const adminProductInput = (index, field, label, value, className = '', type = 'text', step = '1') => `<label class="${className}"><span>${label}</span><input data-admin-product-index="${index}" data-admin-product-field="${field}" type="${type}" ${type === 'number' ? `min="0" step="${step}"` : ''} value="${escapeHtml(value ?? '')}" /></label>`;
const adminImagePreviews = (items, productIndex) => items.length ? `<div class="admin-image-previews">${items.map((file, imageIndex) => `
  <figure class="admin-image-chip">
    ${file.url ? `<img src="${escapeHtml(file.url)}" alt="" />` : '<span>IMG</span>'}
    <button type="button" data-admin-remove-image="${productIndex}:${imageIndex}" aria-label="删除产品图片">×</button>
    <figcaption>${escapeHtml(file.fileName || `图片 ${imageIndex + 1}`)}</figcaption>
  </figure>`).join('')}</div>` : '<div class="admin-image-empty">暂无产品图片</div>';

async function uploadAdminProductFiles(index, files) {
  const product = state.adminEditProducts[index];
  if (!product) return;
  if (!Array.isArray(product.images)) product.images = [];
  const remaining = 9 - product.images.length;
  if (remaining <= 0) return toast('每个产品最多 9 张图片');
  setBusy(true, '正在上传产品图片…');
  try {
    for (const file of [...files].slice(0, remaining)) product.images.push(await uploadImage(file, 'product'));
    renderAdminProductEditors();
    toast('产品图片已上传至 OSS');
  } catch (error) { toast(`图片上传失败：${error.message}`); }
  finally { setBusy(false); }
}

function renderAdminAttachmentEditors() {
  const element = $('#admin-attachment-editors');
  if (!element) return;
  element.innerHTML = `
    <div class="admin-attachment-card">
      <div class="admin-product-images-head"><span>收款截图 · ${state.adminPaymentAttachments.length}/9</span><label class="admin-image-upload"><input type="file" accept="image/*" multiple data-admin-attachment-files="payment" />＋ 上传图片</label></div>
      ${adminAttachmentPreviews(state.adminPaymentAttachments, 'payment', '收款截图')}
    </div>
    <div class="admin-attachment-card">
      <div class="admin-product-images-head"><span>采购凭证图片 / 采购单 · ${state.adminPurchaseAttachments.length}/9</span><label class="admin-image-upload"><input type="file" accept="image/*" multiple data-admin-attachment-files="purchase" />＋ 上传图片</label></div>
      ${adminAttachmentPreviews(state.adminPurchaseAttachments, 'purchase', '采购凭证图片 / 采购单')}
    </div>`;
}

const adminAttachmentPreviews = (items, category, group = '凭证') => items.length ? `<div class="admin-image-previews">${items.map((file, index) => `
  <figure class="admin-image-chip">
    ${isImageAttachment(file) && file.url
      ? `<button type="button" class="admin-image-open" data-image-gallery="${escapeHtml(group)}" data-image-src="${escapeHtml(file.url)}" data-image-name="${escapeHtml(file.fileName || `凭证 ${index + 1}`)}"><img src="${escapeHtml(file.url)}" alt="" /></button>`
      : file.url ? `<a class="admin-image-open" href="${escapeHtml(file.url)}" target="_blank" rel="noreferrer"><span>${isImageAttachment(file) ? 'IMG' : 'FILE'}</span></a>` : '<span class="admin-image-open"><span>IMG</span></span>'}
    <button type="button" data-admin-remove-attachment="${category}:${index}" aria-label="删除凭证">×</button>
    <figcaption>${escapeHtml(file.fileName || `凭证 ${index + 1}`)}</figcaption>
  </figure>`).join('')}</div>` : '<div class="admin-image-empty">暂无凭证</div>';

async function uploadAdminAttachmentFiles(category, files) {
  const target = category === 'purchase' ? state.adminPurchaseAttachments : state.adminPaymentAttachments;
  const remaining = 9 - target.length;
  if (remaining <= 0) return toast('每类凭证最多 9 张图片');
  setBusy(true, '正在上传图片凭证…');
  try {
    for (const file of [...files].slice(0, remaining)) target.push(await uploadImage(file, category));
    renderAdminAttachmentEditors();
    toast('图片凭证已上传至 OSS');
  } catch (error) { toast(`图片上传失败：${error.message}`); }
  finally { setBusy(false); }
}

async function saveAdminOrder(event) {
  event.preventDefault();
  if (state.busy || !state.currentDetail) return;
  const form = new FormData(event.currentTarget);
  const invalid = state.adminEditProducts.some((item) => !String(item.name).trim() || !String(item.variant).trim() || Number(item.quantity) <= 0);
  if (invalid) return toast('产品名称、款式和数量不能为空');
  const body = {
    customerName: form.get('customerName'), customerContact: form.get('customerContact') || null,
    shippingAddress: form.get('shippingAddress') || null, destination: form.get('destination') || null,
    deadline: form.get('deadline'), paymentMethod: form.get('paymentMethod'), tradeTerm: form.get('tradeTerm') || null, currency: String(form.get('currency') || 'USD').toUpperCase(),
    freight: Number(form.get('freight') || 0), purchaseFreight: Number(form.get('purchaseFreight') || 0),
    receivedUsd: form.get('receivedUsd') ? Number(form.get('receivedUsd')) : null,
    exchangeRate: form.get('exchangeRate') ? Number(form.get('exchangeRate')) : null,
    isCompleted: form.get('isCompleted') === 'on', note: form.get('note') || null, ownerUserId: form.get('ownerUserId'),
    products: state.adminEditProducts.map((item) => ({
      ...(item.id ? { id: item.id } : {}), sku: item.sku, name: item.name, variant: item.variant,
      unitsPerCarton: Number(item.unitsPerCarton), cartons: Number(item.cartons), weight: Number(item.weight), volume: Number(item.volume),
      quantity: Number(item.quantity), unitPrice: Number(item.unitPrice), purchaseCost: Number(item.purchaseCost || 0), purchaseStatus: item.purchaseStatus || 'pending',
      images: item.images || []
    })),
    paymentAttachments: state.adminPaymentAttachments,
    purchaseAttachments: state.adminPurchaseAttachments
  };
  // 整单总采购成本留空（null）时由服务端按各款成本合计；该字段仅超级管理员可改。
  if (state.adminEditIsSuperAdmin) {
    const purchaseTotalText = form.get('purchaseTotal');
    body.purchaseTotal = purchaseTotalText === '' || purchaseTotalText === null ? null : Number(purchaseTotalText);
  }
  setBusy(true);
  try {
    const result = await api(`/api/orders/${state.currentDetail.id}`, { method: 'PUT', body: JSON.stringify(body) });
    state.currentDetail = result.order;
    replaceOrder(result.order);
    renderDetail();
    $('#admin-order-dialog').close();
    toast('订单及产品数据已更新');
  } catch (error) { toast(`保存失败：${error.message}`); }
  finally { setBusy(false); }
}

async function deleteAdminOrder(id) {
  const order = state.orders.find((item) => item.id === id) || state.currentDetail;
  const confirmed = await askConfirm({
    title: '删除订单',
    message: `确认删除订单 ${order?.orderNo || id}？该订单的产品、凭证关联、物流和历史记录也会删除，且无法恢复。`,
    confirmText: '删除订单'
  });
  if (!confirmed) return;
  try {
    await api(`/api/orders/${id}`, { method: 'DELETE' });
    state.orders = state.orders.filter((item) => item.id !== id);
    if (state.orderViewItems) state.orderViewItems = state.orderViewItems.filter((item) => item.id !== id);
    closeDetail();
    renderAll();
    toast('订单数据已删除');
  } catch (error) { toast(`删除失败：${error.message}`); }
}

async function deleteAdminShipment() {
  if (!state.currentDetail?.shipment) return;
  const confirmed = await askConfirm({
    title: '删除物流记录',
    message: `确认删除订单 ${state.currentDetail.orderNo} 的物流记录？订单将退回待发货状态。`,
    confirmText: '删除物流'
  });
  if (!confirmed) return;
  try {
    const result = await api(`/api/orders/${state.currentDetail.id}/shipment`, { method: 'DELETE' });
    state.currentDetail = result.order;
    replaceOrder(result.order);
    renderDetail();
    toast('物流记录已删除');
  } catch (error) { toast(`删除物流失败：${error.message}`); }
}

async function loadMembers() {
  if (!state.user?.isDingAdmin) return;
  $('#members-body').innerHTML = '<tr><td colspan="7"><div class="loading-line"></div></td></tr>';
  try {
    const result = await api('/api/admin/users');
    state.members = result.items || result.users || [];
    renderMembers();
    if (result.sync?.ok === false) toast(`钉钉通讯录同步失败，当前显示本地数据：${result.sync.error}`);
  } catch (error) { $('#members-body').innerHTML = `<tr><td colspan="6">读取失败：${escapeHtml(error.message)}</td></tr>`; }
}

function renderMembers() {
  $('#members-body').innerHTML = state.members.map((member) => `<tr data-member-id="${member.id}">
    <td><div class="member-info"><div class="avatar">${escapeHtml((member.name || '·').slice(0,1))}</div><div><strong>${escapeHtml(member.name)}${member.isDingAdmin ?? member.is_ding_admin ? '<em class="ding-admin-mark">超级管理员</em>' : ''}</strong><span>${escapeHtml(member.title || member.mobile || '未填写职位')}</span></div></div></td>
    <td class="muted">${escapeHtml(member.dingUserId || member.ding_user_id || '—')}</td>
    <td><select class="role-select" data-member-role="${member.id}" ${member.id === state.user.id || (member.isDingAdmin ?? member.is_ding_admin) ? 'disabled' : ''}>${Object.entries(roleNames).map(([value, label]) => `<option value="${value}" ${member.role === value ? 'selected' : ''}>${label}</option>`).join('')}</select></td>
    <td>${['sales', 'admin'].includes(member.role) ? `<input class="commission-input" data-member-commission="${member.id}" type="number" min="0" max="100" step="0.01" value="${Number(member.commissionRatePercent ?? member.commission_rate_percent ?? 0)}" aria-label="${escapeHtml(member.name)}提成比例" /> %` : '—'}</td>
    <td><button class="switch ${member.dingNotificationEnabled ?? member.ding_notification_enabled ? 'on' : ''}" data-member-notification="${member.id}" aria-label="切换钉钉通知"></button></td>
    <td>${member.lastLoginAt || member.last_login_at ? escapeHtml(String(member.lastLoginAt || member.last_login_at).replace('T',' ').slice(0,16)) : '尚未登录'}</td>
    <td><button class="switch ${member.isActive ?? member.is_active ? 'on' : ''}" data-member-active="${member.id}" ${member.id === state.user.id ? 'disabled' : ''} aria-label="切换账号状态"></button></td>
  </tr>`).join('');
}

async function changeMemberRole(id, role) {
  try { await api(`/api/admin/users/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }); toast('成员角色已更新'); await loadMembers(); }
  catch (error) { toast(`角色更新失败：${error.message}`); await loadMembers(); }
}

async function changeMemberCommission(id, commissionRatePercent) {
  try {
    await api(`/api/admin/users/${id}/commission`, {
      method: 'PATCH', body: JSON.stringify({ commissionRatePercent: Number(commissionRatePercent) })
    });
    toast('提成比例已更新');
  } catch (error) {
    toast(`提成比例更新失败：${error.message}`);
    await loadMembers();
  }
}

async function toggleMember(id) {
  const member = state.members.find((item) => item.id === id);
  if (!member) return;
  const active = Boolean(member.isActive ?? member.is_active);
  try { await api(`/api/admin/users/${id}/active`, { method: 'PATCH', body: JSON.stringify({ isActive: !active }) }); toast(!active ? '账号已启用' : '账号已停用'); await loadMembers(); }
  catch (error) { toast(`账号更新失败：${error.message}`); }
}

async function toggleMemberNotification(id) {
  const member = state.members.find((item) => item.id === id);
  if (!member) return;
  const enabled = Boolean(member.dingNotificationEnabled ?? member.ding_notification_enabled);
  try {
    await api(`/api/admin/users/${id}/notification`, { method: 'PATCH', body: JSON.stringify({ enabled: !enabled }) });
    toast(enabled ? '已关闭该成员钉钉通知' : '已开启该成员钉钉通知');
    await loadMembers();
  } catch (error) { toast(`通知设置失败：${error.message}`); }
}

const imageViewer = { items: [], index: 0, label: '凭证预览' };

function renderImageViewer() {
  const current = imageViewer.items[imageViewer.index];
  if (!current) return;
  $('#image-viewer-img').src = current.src;
  $('#image-viewer-img').alt = current.name;
  $('#image-viewer-title').textContent = imageViewer.label;
  $('#image-viewer-count').textContent = `${imageViewer.index + 1} / ${imageViewer.items.length}`;
  $('#image-viewer-open').href = current.src;
  $('#image-viewer-download').href = current.src;
  $('#image-viewer-download').setAttribute('download', current.name);
  const multiple = imageViewer.items.length > 1;
  $('#image-viewer-prev').classList.toggle('is-hidden', !multiple);
  $('#image-viewer-next').classList.toggle('is-hidden', !multiple);
  $('#image-viewer-strip').innerHTML = multiple ? imageViewer.items.map((item, index) => `
    <button type="button" class="image-viewer-thumb ${index === imageViewer.index ? 'active' : ''}" data-image-viewer-index="${index}" aria-label="${escapeHtml(item.name)}">
      <img src="${escapeHtml(item.src)}" alt="" />
    </button>`).join('') : '';
}

function stepImageViewer(offset) {
  if (imageViewer.items.length < 2) return;
  imageViewer.index = (imageViewer.index + offset + imageViewer.items.length) % imageViewer.items.length;
  renderImageViewer();
}

function openImageViewer(target) {
  const group = target.dataset.imageGallery || '';
  const tiles = group
    ? Array.from(document.querySelectorAll('[data-image-gallery]')).filter((tile) => (tile.dataset.imageGallery || '') === group)
    : [target];
  const items = tiles.map((tile) => ({
    src: tile.dataset.imageSrc,
    name: tile.dataset.imageName || '凭证'
  })).filter((item) => item.src);
  if (!items.length) return;
  const active = target.dataset.imageSrc;
  imageViewer.items = items;
  imageViewer.index = Math.max(0, items.findIndex((item) => item.src === active));
  imageViewer.label = group || '凭证预览';
  renderImageViewer();
  $('#image-viewer').classList.remove('is-hidden');
}

function closeImageViewer() {
  $('#image-viewer').classList.add('is-hidden');
  $('#image-viewer-img').removeAttribute('src');
  imageViewer.items = [];
  imageViewer.index = 0;
}

function bindImageViewerEvents() {
  $('#image-viewer-close').addEventListener('click', closeImageViewer);
  $('#image-viewer-prev').addEventListener('click', () => stepImageViewer(-1));
  $('#image-viewer-next').addEventListener('click', () => stepImageViewer(1));
  $('#image-viewer-strip').addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-image-viewer-index]');
    if (!thumb) return;
    imageViewer.index = Number(thumb.dataset.imageViewerIndex);
    renderImageViewer();
  });
  $('#image-viewer').addEventListener('click', (event) => {
    if (event.target === $('#image-viewer') || event.target.id === 'image-viewer-img') closeImageViewer();
  });
  document.addEventListener('keydown', (event) => {
    if ($('#image-viewer').classList.contains('is-hidden')) return;
    if (event.key === 'Escape') closeImageViewer();
    else if (event.key === 'ArrowLeft') stepImageViewer(-1);
    else if (event.key === 'ArrowRight') stepImageViewer(1);
  });
}

function bindEvents() {
  $('#auth-retry').addEventListener('click', authenticate);
  $('#logout-button').addEventListener('click', () => logout());
  $('#confirm-dialog').addEventListener('close', () => {
    const resolve = confirmRequest.resolve;
    confirmRequest.resolve = null;
    if (resolve) resolve($('#confirm-dialog').returnValue === 'confirm');
  });
  $$('.nav-item').forEach((button) => button.addEventListener('click', () => switchView(button.dataset.view)));
  $$('[data-view-jump]').forEach((button) => button.addEventListener('click', () => switchView(button.dataset.viewJump)));
  $('#create-button').addEventListener('click', openCreate);
  $('#admin-create-order').addEventListener('click', openCreate);
  $('#refresh-button').addEventListener('click', async () => { await loadOrders(); if (state.orderDateFrom || state.orderDateTo) await loadOrderView(); });
  $('#order-date-from').addEventListener('change', (event) => { state.orderDateFrom = event.target.value; loadOrderView(); });
  $('#order-date-to').addEventListener('change', (event) => { state.orderDateTo = event.target.value; loadOrderView(); });
  $$('[data-trade-term-note]').forEach((select) => select.addEventListener('change', () => {
    const note = $(`#${select.dataset.tradeTermNote}`);
    if (note) note.textContent = tradeTermNoteText(select.value);
  }));
  $('#clear-order-dates').addEventListener('click', () => {
    state.orderDateFrom = '';
    state.orderDateTo = '';
    state.orderViewItems = null;
    $('#order-date-from').value = '';
    $('#order-date-to').value = '';
    renderOrders();
  });
  $('#performance-month').addEventListener('change', (event) => {
    state.performanceMonth = event.target.value || currentMonth();
    loadPerformance();
  });
  $('#performance-export-button').addEventListener('click', exportPerformanceExcel);
  $('#global-search').addEventListener('input', (event) => { state.search = event.target.value.trim(); renderOrders(); if (state.currentView === 'dashboard') switchView('orders'); });
  $('#global-owner-filter').addEventListener('change', (event) => {
    state.ownerFilter = event.target.value;
    renderAll();
    if (state.currentView === 'performance') loadPerformance();
  });
  $('#order-filters').addEventListener('click', (event) => {
    const button = event.target.closest('[data-status]'); if (!button) return;
    state.orderFilter = button.dataset.status;
    $$('#order-filters button').forEach((item) => item.classList.toggle('active', item === button));
    renderOrders();
  });
  document.addEventListener('click', (event) => {
    const imageTile = event.target.closest('[data-image-gallery]');
    if (imageTile) { event.preventDefault(); event.stopPropagation(); openImageViewer(imageTile); return; }
    const adminEdit = event.target.closest('[data-admin-edit]');
    if (adminEdit) { event.stopPropagation(); openAdminOrderEditor(adminEdit.dataset.adminEdit); return; }
    const adminDelete = event.target.closest('[data-admin-delete]');
    if (adminDelete) { event.stopPropagation(); deleteAdminOrder(adminDelete.dataset.adminDelete); return; }
    const orderElement = event.target.closest('[data-order-id]');
    if (orderElement && !event.target.closest('[data-purchase-product]')) openDetail(orderElement.dataset.orderId);
    const close = event.target.closest('.modal-close'); if (close) { event.preventDefault(); close.closest('dialog').close(); }
    const remove = event.target.closest('[data-remove-file]');
    if (remove) {
      const [category, indexText] = remove.dataset.removeFile.split(':');
      const target = category === 'payment' ? state.paymentAttachments
        : category === 'purchase' ? state.purchaseAttachments
          : category === 'expense' ? state.expenseAttachments : state.shipmentAttachments;
      target.splice(Number(indexText), 1);
      if (category === 'payment') renderPaymentPreviews();
      else if (category === 'purchase') renderPurchasePreviews();
      else if (category === 'expense') renderExpensePreviews();
      else renderShipmentPreviews();
    }
    const expenseDecision = event.target.closest('[data-expense-decision]');
    if (expenseDecision) {
      const [id, status] = expenseDecision.dataset.expenseDecision.split(':');
      decideExpense(id, status);
    }
    const expenseReimbursed = event.target.closest('[data-expense-reimbursed]');
    if (expenseReimbursed) toggleExpenseReimbursed(expenseReimbursed.dataset.expenseReimbursed);
  });
  $('#close-detail').addEventListener('click', closeDetail);
  $('#drawer-mask').addEventListener('click', closeDetail);
  $('#detail-drawer').addEventListener('click', (event) => {
    const product = event.target.closest('[data-purchase-product]'); if (product) completePurchase([product.dataset.purchaseProduct]);
    if (event.target.closest('[data-purchase-all]')) completePurchase();
    if (event.target.closest('[data-submit-purchase]')) submitPurchase();
    if (event.target.closest('[data-confirm-payment]')) confirmPayment();
    if (event.target.closest('[data-export-order]')) exportOrderExcel();
    if (event.target.closest('[data-open-shipment]')) openShipment(state.currentDetail?.status === 'shipped' ? 'edit' : 'draft');
    if (event.target.closest('[data-confirm-shipment]')) submitShipmentConfirm();
    if (event.target.closest('[data-admin-edit-detail]')) openAdminOrderEditor(state.currentDetail.id);
    if (event.target.closest('[data-admin-delete-detail]')) deleteAdminOrder(state.currentDetail.id);
    if (event.target.closest('[data-admin-edit-shipment]')) openShipment('edit');
    if (event.target.closest('[data-admin-delete-shipment]')) deleteAdminShipment();
  });
  $('#detail-drawer').addEventListener('change', (event) => {
    if (event.target.matches('#purchase-files') && event.target.files?.length) uploadAttachments(event.target.files, 'purchase');
    const supplement = event.target.closest('[data-order-attachment-files]');
    if (supplement?.files?.length) {
      uploadOrderSupplementAttachments(supplement.dataset.orderAttachmentFiles, supplement.files);
      supplement.value = '';
    }
  });
  $('#add-product').addEventListener('click', () => { state.productDrafts.push(emptyProduct()); renderProductEditors(); });
  $('#import-pi').addEventListener('click', () => $('#pi-file-input').click());
  $('#pi-file-input').addEventListener('change', (event) => importPiFile(event.target.files?.[0]));
  $('#product-editors').addEventListener('input', (event) => {
    const input = event.target.closest('[data-product-field]'); if (!input) return;
    state.productDrafts[Number(input.dataset.productIndex)][input.dataset.productField] = input.value;
  });
  $('#product-editors').addEventListener('change', (event) => {
    const input = event.target.closest('[data-product-files]'); if (input?.files?.length) uploadProductFiles(Number(input.dataset.productFiles), input.files);
  });
  $('#product-editors').addEventListener('click', (event) => {
    const button = event.target.closest('[data-remove-product]'); if (!button) return;
    state.productDrafts.splice(Number(button.dataset.removeProduct), 1); renderProductEditors();
  });
  $('#payment-files').addEventListener('change', (event) => {
    const files = [...(event.target.files || [])];
    event.target.value = '';
    if (files.length) uploadAttachments(files, 'payment');
  });
  $('#shipment-files').addEventListener('change', (event) => uploadAttachments(event.target.files, 'logistics'));
  $('#expense-files').addEventListener('change', (event) => uploadAttachments(event.target.files, 'expense'));
  $('#open-expense-dialog').addEventListener('click', openExpenseDialog);
  $('#expense-form').addEventListener('submit', submitExpense);
  $('#create-form').addEventListener('submit', submitOrder);
  $('#shipment-form').addEventListener('submit', submitShipment);
  $('#save-shipment-draft').addEventListener('click', () => { state.shipmentAction = 'save'; });
  $('#save-shipment').addEventListener('click', () => { state.shipmentAction = state.shipmentMode === 'edit' ? 'update' : 'dispatch'; });
  $('#admin-order-form').addEventListener('submit', saveAdminOrder);
  $('#admin-add-product').addEventListener('click', () => {
    state.adminEditProducts.push({ ...emptyProduct(), purchaseStatus: 'pending' });
    renderAdminProductEditors();
  });
  $('#admin-product-editors').addEventListener('input', (event) => {
    const input = event.target.closest('[data-admin-product-field]'); if (!input) return;
    state.adminEditProducts[Number(input.dataset.adminProductIndex)][input.dataset.adminProductField] = input.value;
    if (input.dataset.adminProductField === 'purchaseCost') syncAdminPurchaseTotalHint();
  });
  $('#admin-product-editors').addEventListener('change', (event) => {
    const files = event.target.closest('[data-admin-product-files]');
    if (files?.files?.length) {
      uploadAdminProductFiles(Number(files.dataset.adminProductFiles), files.files);
      files.value = '';
      return;
    }
    const input = event.target.closest('[data-admin-product-field]'); if (!input) return;
    state.adminEditProducts[Number(input.dataset.adminProductIndex)][input.dataset.adminProductField] = input.value;
  });
  $('#admin-product-editors').addEventListener('click', (event) => {
    const removeImage = event.target.closest('[data-admin-remove-image]');
    if (removeImage) {
      const [productIndex, imageIndex] = removeImage.dataset.adminRemoveImage.split(':').map(Number);
      state.adminEditProducts[productIndex]?.images?.splice(imageIndex, 1);
      renderAdminProductEditors();
      return;
    }
    const button = event.target.closest('[data-admin-remove-product]'); if (!button) return;
    state.adminEditProducts.splice(Number(button.dataset.adminRemoveProduct), 1);
    renderAdminProductEditors();
  });
  $('#admin-attachment-editors').addEventListener('change', (event) => {
    const input = event.target.closest('[data-admin-attachment-files]');
    if (!input?.files?.length) return;
    uploadAdminAttachmentFiles(input.dataset.adminAttachmentFiles, input.files);
    input.value = '';
  });
  $('#admin-attachment-editors').addEventListener('click', (event) => {
    const button = event.target.closest('[data-admin-remove-attachment]');
    if (!button) return;
    const [category, indexText] = button.dataset.adminRemoveAttachment.split(':');
    const target = category === 'purchase' ? state.adminPurchaseAttachments : state.adminPaymentAttachments;
    target.splice(Number(indexText), 1);
    renderAdminAttachmentEditors();
  });
  $('#admin-refresh').addEventListener('click', loadMembers);
  $('#members-body').addEventListener('change', (event) => {
    const select = event.target.closest('[data-member-role]');
    if (select) changeMemberRole(select.dataset.memberRole, select.value);
    const commission = event.target.closest('[data-member-commission]');
    if (commission) changeMemberCommission(commission.dataset.memberCommission, commission.value);
  });
  $('#members-body').addEventListener('click', (event) => {
    const button = event.target.closest('[data-member-active]'); if (button) toggleMember(button.dataset.memberActive);
    const notification = event.target.closest('[data-member-notification]'); if (notification) toggleMemberNotification(notification.dataset.memberNotification);
  });
  $('#expense-list').addEventListener('change', (event) => {
    const input = event.target.closest('[data-expense-payment-files]');
    if (input) uploadExpensePaymentProofs(input.dataset.expensePaymentFiles, input.files || []);
  });
}

initializeMonthPickers();
bindEvents();
bindImageViewerEvents();
setMonthPickerValue($('[data-month-picker="performance-export-month"]'), currentMonth());
if (previewMode) {
  state.user = { id: 'preview-admin', name: '林航', role: 'admin', title: '外贸业务总监', avatarUrl: '' };
  state.rates = {
    monthStart: { date: '2026-08-03', rate: 6.7476 },
    today: { date: '2026-08-10', rate: 6.7439 }
  };
  state.performance = {
    month: '2026-08',
    summary: { orderCount: 3, revenueCny: 579078, profitCny: 142632, commissionCny: 4516.46, completedCommissionCny: 2680.20 },
    warnings: [],
    items: [
      { id: 'preview-1', orderNo: 'SO-260810-A1F92C', orderDate: '2026-08-10', customerName: 'Nordhavn Living', ownerName: '林航', currency: 'USD', orderAmount: 28640, receivedUsd: null, revenueCny: 193174, convertedOrderAmountCny: 193174, productCost: 17800, sellerFreight: 1320, shippingFreight: 980, profitCny: 64433, commissionRatePercent: 3.2, commissionCny: 2061.86, freightForwarder: '宁波远洋', isCompleted: false },
      { id: 'preview-2', orderNo: 'SO-260809-8C70D4', orderDate: '2026-08-09', customerName: 'Maison Épure', ownerName: '周黎', currency: 'USD', orderAmount: 15820, receivedUsd: null, revenueCny: 106704, convertedOrderAmountCny: 106704, productCost: 9600, sellerFreight: 880, shippingFreight: 720, profitCny: 36171, commissionRatePercent: 2.5, commissionCny: 904.28, freightForwarder: '海程国际', isCompleted: true },
      { id: 'preview-3', orderNo: 'SO-260805-3D8E11', orderDate: '2026-08-05', customerName: 'Atelier Form', ownerName: '林航', currency: 'USD', orderAmount: 42100, receivedUsd: 41200, revenueCny: 279200, convertedOrderAmountCny: 284080, productCost: 26800, sellerFreight: 2300, shippingFreight: 1850, profitCny: 42028, commissionRatePercent: 3.2, commissionCny: 1344.90, freightForwarder: '迅达货运', isCompleted: true }
    ]
  };
  state.leaderboardReport = {
    month: '2026-08',
    champion: { userId: 'preview-admin', name: '林航', title: '外贸业务总监', avatarUrl: '', rank: 1, orderCount: 4, salesCny: 476820, profitCny: 108640, commissionCny: 3476.48 },
    leaders: [
      { userId: 'preview-admin', name: '林航', title: '外贸业务总监', avatarUrl: '', rank: 1, orderCount: 4, salesCny: 476820, profitCny: 108640, commissionCny: 3476.48 },
      { userId: 'preview-sales-2', name: '周黎', title: '外贸业务员', avatarUrl: '', rank: 2, orderCount: 3, salesCny: 328460, profitCny: 74120, commissionCny: 1853 },
      { userId: 'preview-sales-3', name: '唐允', title: '外贸业务员', avatarUrl: '', rank: 3, orderCount: 2, salesCny: 186900, profitCny: 39540, commissionCny: 988.5 }
    ],
    me: { userId: 'preview-admin', name: '林航', title: '外贸业务总监', avatarUrl: '', rank: 1, orderCount: 4, salesCny: 476820, profitCny: 108640, commissionCny: 3476.48 }
  };
  state.orders = [
    { id: 'preview-1', orderNo: 'SO-260810-A1F92C', createdAt: '2026-08-10T09:20:00', customerName: 'Nordhavn Living', destination: '丹麦', deadline: '2026-08-12', currency: 'USD', totalAmount: 28640, ownerName: '林航', status: 'purchasing', productCount: 3, productSummary: { sku: 'BLK-072', name: '云朵绒毯', variant: '奶油白' } },
    { id: 'preview-2', orderNo: 'SO-260809-8C70D4', createdAt: '2026-08-09T14:10:00', customerName: 'Maison Épure', destination: '法国', deadline: '2026-08-14', currency: 'USD', totalAmount: 15820, ownerName: '周黎', status: 'purchased', productCount: 2, productSummary: { sku: 'CER-118', name: '手工陶瓷餐具', variant: '雾蓝釉' } },
    { id: 'preview-3', orderNo: 'SO-260805-3D8E11', createdAt: '2026-08-05T11:30:00', customerName: 'Atelier Form', destination: '澳大利亚', deadline: '2026-08-20', currency: 'USD', totalAmount: 42100, ownerName: '林航', status: 'shipped', productCount: 4, productSummary: { sku: 'LMP-042', name: '纸艺吊灯', variant: '原木色' } },
    { id: 'preview-4', orderNo: 'SO-260810-F09A27', createdAt: '2026-08-10T16:40:00', customerName: 'North & Pine', destination: '加拿大', deadline: '2026-08-11', currency: 'USD', totalAmount: 9340, ownerName: '唐允', status: 'pending_purchase', productCount: 1, productSummary: { sku: 'BAG-220', name: '帆布旅行包', variant: '橄榄绿' } }
  ];
  enterApp();
  renderExchangeRates();
} else {
  authenticate();
}
